import { Slide, Theme, AspectRatio, SlideElement, ThemeFont, SlideBorderStyle, FontSizes, ChartData, SlideContentItem, EChartsOption } from '../types';
import { generateSlidesWithGemini, generateSlidesWithGeminiAndUsage, updateSlideWithGemini, TokenUsage } from './geminiApi';
import { SlideType } from '../components/SlideTypeSelector';
import { getBackgroundForContent, createDefaultBackgroundOptions, generatePicsumImage } from './imageSearch';
import { searchPixabayImages, getPixabayImageUrl } from './pixabayApi';
import { getThemeColors } from './themeColorUtils';

// AI가 생성한 차트 데이터를 ChartData 형식으로 변환하는 함수
const convertToChartData = (rawData: unknown): ChartData | null => {
  try {
    if (!rawData || typeof rawData !== 'object') {return null;}
    
    const data = rawData as Record<string, unknown>;
    
    // AI가 생성한 표준 형식: {type, title, data: {labels, values}}
    if (data.type && data.data && typeof data.data === 'object') {
      const chartData = data.data as Record<string, unknown>;
      if (chartData.labels && chartData.values) {
        return {
          type: data.type as ChartData['type'],
          data: {
            labels: Array.isArray(chartData.labels) ? chartData.labels as string[] : [String(chartData.labels)],
            values: Array.isArray(chartData.values) ? chartData.values as (number[] | number[][]) : [Number(chartData.values)]
          },
          title: String(data.title || '차트')
        };
      }
    }
    
    // 이전 형식도 지원: {title, labels, data, unit} 등
    if (data.labels && data.data) {
      return {
        type: (data.type as ChartData['type']) || 'bar',
        data: {
          labels: Array.isArray(data.labels) ? data.labels as string[] : [String(data.labels)],
          values: Array.isArray(data.data) ? data.data as (number[] | number[][]) : [Number(data.data)]
        },
        title: String(data.title || '차트')
      };
    }
    
    // 다른 형식들도 처리
    if (data.categories && data.values) {
      return {
        type: 'bar',
        data: {
          labels: Array.isArray(data.categories) ? data.categories as string[] : [String(data.categories)],
          values: Array.isArray(data.values) ? data.values as (number[] | number[][]) : [Number(data.values)]
        },
        title: String(data.title || '차트')
      };
    }
    
    // 시계열 데이터 처리
    if (data.years && data.data) {
      return {
        type: 'line',
        data: {
          labels: Array.isArray(data.years) ? data.years as string[] : [String(data.years)],
          values: Array.isArray(data.data) ? data.data as (number[] | number[][]) : [Number(data.data)]
        },
        title: String(data.title || '차트')
      };
    }
    
    // 시리즈 데이터가 있는 경우
    if (data.series && Array.isArray(data.series)) {
      const firstSeries = data.series[0] as Record<string, unknown>;
      return {
        type: 'bar',
        data: {
          labels: (firstSeries.labels as string[]) || [],
          values: (firstSeries.data as (number[] | number[][])) || [],
          series: (data.series as Array<{ name?: string; data?: number[] | number[][] }>).map((s) => ({
            name: s.name || '시리즈',
            data: s.data || []
          }))
        },
        title: String(data.title || '차트')
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error converting chart data:', error);
    return null;
  }
};
import { 
  getLayoutOptionsForObjectCount, 
  countContentObjects, 
  getContentArea, 
  calculateObjectPositions, 
  getDefaultLayoutOption,
  LayoutOption 
} from './dynamicLayout';

// Memoized safe stringify function
const safeStringifyCache = new WeakMap<object, string>();

// 객체를 안전하게 문자열로 변환하는 헬퍼 함수
const safeStringify = (content: unknown): string => {
  if (typeof content === 'string') {return content;}
  if (content === null || content === undefined) {return '';}
  
  if (typeof content === 'object') {
    // Check cache first
    if (safeStringifyCache.has(content)) {
      return safeStringifyCache.get(content)!;
    }
    
    const result = '';
    
    // 배열인 경우 - 각 항목을 줄바꿈으로 구분
    if (Array.isArray(content)) {
      return content.map(item => {
        if (typeof item === 'string') {return item;}
        if (typeof item === 'object' && item !== null) {
          // 객체인 경우 주요 속성들을 추출
          if (item.text) {return item.text;}
          if (item.title) {return item.title;}
          if (item.content) {return item.content;}
          if (item.description) {return item.description;}
          if (item.value) {return item.value;}
          // 단순 객체라면 값들을 연결
          const values = Object.values(item).filter(v => typeof v === 'string' && v.trim());
          if (values.length > 0) {return values.join(': ');}
        }
        return String(item);
      }).filter(item => item && item.trim()).join('\n• '); // 불릿 포인트로 구분
      
      safeStringifyCache.set(content, result);
      return result;
    }
    
    // 일반적인 텍스트 속성들 확인
    if (content.text) {return String(content.text);}
    if (content.value) {return String(content.value);}
    if (content.title) {return String(content.title);}
    if (content.description) {return String(content.description);}
    if (content.content) {return safeStringify(content.content);}
    
    // 객체가 비어있지 않다면 의미있는 값들을 추출
    try {
      const values = Object.values(content).filter(v => 
        v !== null && 
        v !== undefined && 
        typeof v === 'string' && 
        v.trim().length > 0
      );
      
      if (values.length > 0) {
        return values.join('\n• ');
      }
      
      // 복잡한 객체의 경우 key-value 형태로
      const entries = Object.entries(content);
      if (entries.length > 0 && entries.every(([key, value]) => typeof value !== 'object')) {
        return entries.map(([key, value]) => `${key}: ${value}`).join('\n• ');
      }
    } catch (e) {
      // 변환 실패 시 빈 문자열 반환
    }
    
    // Cache empty result too
    safeStringifyCache.set(content, result);
    return result;
  }
  
  return String(content);
};

// isTitle 함수를 dynamicLayout에서 export하도록 수정 필요
const isTitle = (element: SlideElement, slideHeight = 600): boolean => {
  if (element.type === 'title') {return true;}
  
  const yRatio = element.y || 0;
  const isRatioBased = yRatio <= 1;
  
  if (isRatioBased) {
    return yRatio < 0.1 && element.fontSize > 24;
  } else {
    const pixelYRatio = yRatio / slideHeight;
    return pixelYRatio < 0.1 && element.fontSize > 24;
  }
};

// 랜덤 레이아웃 옵션 선택
const getRandomLayoutOption = (objectCount: number): LayoutOption => {
  const options = getLayoutOptionsForObjectCount(objectCount);
  if (options.length === 0) {
    return getDefaultLayoutOption(objectCount);
  }
  const randomIndex = Math.floor(Math.random() * options.length);
  return options[randomIndex];
};

// Helper function to calculate dynamic layout positions
const calculateDynamicLayoutPosition = (
  elements: SlideElement[], 
  aspectRatio: AspectRatio, 
  slideTheme = 'default',
  splitPosition = 50,
  selectedLayoutOption?: LayoutOption
) => {
  const objectCount = countContentObjects(elements);
  
  if (objectCount === 0) {
    return [];
  }
  
  // 레이아웃 옵션 결정
  const layoutOption = selectedLayoutOption || getDefaultLayoutOption(objectCount);
  
  // 콘텐츠 영역 계산
  const contentArea = getContentArea(aspectRatio, slideTheme, splitPosition);
  
  // 객체 위치 계산
  const positions = calculateObjectPositions(layoutOption, objectCount, contentArea, aspectRatio.width, aspectRatio.height);
  
  return positions;
};

// 레거시 호환성을 위한 함수 (기존 코드에서 사용 중인 경우)
const calculateGridPosition = (index: number, totalItems: number, aspectRatio: AspectRatio) => {
  // 임시 요소 배열 생성 (레거시 호환)
  const tempElements = Array.from({ length: totalItems }, (_, i) => ({
    id: `temp-${i}`,
    type: 'div' as const,
    content: '',
    x: 0,
    y: 0,
    fontSize: 16,
    fontFamily: 'Arial',
    color: '#000000',
    fontWeight: 'normal' as const,
    textAlign: 'left' as const,
    zIndex: 1
  }));
  
  const positions = calculateDynamicLayoutPosition(tempElements, aspectRatio);
  
  if (index < positions.length) {
    return {
      x: positions[index].x / aspectRatio.width,
      y: positions[index].y / aspectRatio.height,
      width: positions[index].width / aspectRatio.width,
      height: positions[index].height / aspectRatio.height
    };
  }
  
  // 폴백
  return {
    x: 0.1,
    y: 0.1 + (index * 0.8 / totalItems),
    width: 0.8,
    height: 0.8 / totalItems
  };
};

// 구조화된 콘텐츠를 HTML 요소로 변환
export const createStructuredElements = async (
  structuredContent: SlideContentItem[],
  theme: Theme,
  aspectRatio: AspectRatio,
  themeFont?: ThemeFont,
  layout = 'title-top-content-bottom',
  slideKeywords?: string[]
): Promise<SlideElement[]> => {
  const elements: SlideElement[] = [];
  
  // 제목과 내용 분리 (제목은 상단 고정, 내용만 동적 레이아웃)
  const titleObjs = structuredContent.filter(item => item.category === 'title');
  const contentObjs = structuredContent.filter(item => item.category === 'content');
  
  // 1. 제목 요소들을 상단 15% 영역에 고정 배치 (반응형 가로폭)
  titleObjs.forEach((titleObj, index) => {
    const titleContent = typeof titleObj.content === 'string' ? 
      titleObj.content : 
      titleObj.content?.items ? 
        titleObj.content.items.map((item: { emoji?: string; heading?: string; description?: string }) => 
          `${item.emoji || ''} ${item.heading || ''}\n${item.description || ''}`.trim()
        ).join('\n\n') :
      safeStringify(titleObj.content || '');
      
    if (titleContent.trim()) {
      // 제목 길이에 따른 반응형 가로폭 계산
      const titleLength = titleContent.replace(/\n/g, ' ').length;
      const minWidth = 0.3; // 최소 30%
      const maxWidth = 0.9; // 최대 90%
      const responsiveWidth = Math.min(maxWidth, Math.max(minWidth, titleLength * 0.02 + 0.2));
      const xPosition = (1 - responsiveWidth) / 2; // 중앙 정렬
      
      elements.push({
        id: `title-${Date.now()}-${index}`,
        type: 'text',
        textType: 'title', // 제목임을 명시
        content: titleContent,
        x: xPosition,  // 반응형 x 위치 (중앙 정렬)
        y: 0.025 + (index * 0.12), // 상단 2.5%부터 시작, 제목이 여러개면 아래로
        width: responsiveWidth, // 반응형 가로폭
        height: 0.12, // 높이 12% (15% 영역 내)
        fontSize: Math.max(aspectRatio.height * 0.04, 24),
        fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
        color: theme.secondary,
        fontWeight: 'bold',
        textAlign: 'center',
        zIndex: 10,
        backgroundColor: theme.primary + '20'
      });
    }
  });
  
  // 2. 내용 요소들만 동적 레이아웃으로 배치 (제목 영역 아래)
  if (contentObjs.length > 0) {
    // 임시 요소들 생성하여 동적 레이아웃 위치 계산
    const tempElements: SlideElement[] = contentObjs.map((contentObj, index) => ({
      id: `temp-${index}`,
      type: contentObj.designStyle === 'chart' ? 'chart' : 
            contentObj.designStyle?.includes('list') ? 'list' : 'text',
      content: '',
      x: 0,
      y: 0,
      fontSize: 16,
      fontFamily: 'Arial',
      color: '#000000',
      fontWeight: 'normal',
      textAlign: 'left' as const,
      zIndex: 1
    }));
    
    // 제목 영역을 제외한 나머지 영역에서 동적 레이아웃 계산
    const contentAreaRatio = aspectRatio; // 전체 영역 사용하되 Y 좌표만 조정
    const dynamicPositions = calculateDynamicLayoutPosition(tempElements, contentAreaRatio);
    
    for (let index = 0; index < contentObjs.length; index++) {
      const contentObj = contentObjs[index];
      let x, y, width, height;
      
      if (index < dynamicPositions.length) {
        // 동적 레이아웃 위치 사용하되 Y 좌표를 제목 영역 아래로 조정
        x = dynamicPositions[index].x / aspectRatio.width;
        y = (dynamicPositions[index].y / aspectRatio.height) * 0.75 + 0.2; // 제목 영역(20%) 아래부터 시작, 75% 영역 사용
        width = dynamicPositions[index].width / aspectRatio.width;
        height = (dynamicPositions[index].height / aspectRatio.height) * 0.75; // 높이도 75% 영역에 맞게 조정
      } else {
        // 폴백: 균등 분할 (제목 영역 아래)
        const itemsPerRow = Math.ceil(Math.sqrt(contentObjs.length));
        const row = Math.floor(index / itemsPerRow);
        const col = index % itemsPerRow;
        
        x = 0.05 + (col * 0.9 / itemsPerRow);
        y = 0.2 + (row * 0.75 / Math.ceil(contentObjs.length / itemsPerRow)); // 제목 영역 아래부터
        width = 0.9 / itemsPerRow - 0.02;
        height = 0.75 / Math.ceil(contentObjs.length / itemsPerRow) - 0.02;
      }
    
      // 콘텐츠 타입에 따른 요소 생성 (동적 레이아웃 위치 사용)
      const elementType = contentObj.format === 'container' ? 'container' :
                         contentObj.designStyle === 'chart' || contentObj.format === 'chart' ? 'chart' : 
                         contentObj.designStyle === 'image' || contentObj.format === 'image' ? 'image' :
                         contentObj.designStyle?.includes('list') ? 'list' : 'text';
      
      const fontSize = Math.max(aspectRatio.height * 0.025, 16);
      
      switch (elementType) {
        case 'list': {
          const listContent = Array.isArray(contentObj.content?.items) ? 
            contentObj.content.items.map((item: { description?: string; heading?: string }) => item.description || item.heading || '').filter(Boolean) :
            Array.isArray(contentObj.content) ? contentObj.content : [];
            
          if (listContent.length > 0) {
            elements.push({
              id: `list-${Date.now()}-${index}`,
              type: 'list',
              content: contentObj.emoji || '📌',
              listItems: listContent,
              listType: contentObj.designStyle === 'numbered-list' ? 'number' : 'emoji',
              x,
              y,
              width,
              height,
              fontSize,
              fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
              color: theme.secondary,
              fontWeight: 'normal',
              textAlign: 'left',
              zIndex: 5,
              backgroundColor: theme.secondary + '20',
              verticalAlign: 'middle'
            });
          }
          break;
        }
          
        case 'chart': {
          // AI가 생성한 차트 데이터를 ChartData 형식으로 변환
          let chartData = null;
          
          console.log('Processing chart object:', {
            format: contentObj.format,
            contentType: typeof contentObj.content,
            content: contentObj.content
          });
          
          // contentObj.content가 문자열인 경우 JSON 파싱 시도
          if (typeof contentObj.content === 'string') {
            try {
              const parsedContent = JSON.parse(contentObj.content);
              chartData = convertToChartData(parsedContent);
            } catch (error) {
              console.error('Failed to parse chart content as JSON:', error);
            }
          } 
          // contentObj.content가 객체인 경우
          else if (typeof contentObj.content === 'object' && contentObj.content) {
            // 이미 chartData 형식인 경우
            if (contentObj.content.chartData) {
              chartData = contentObj.content.chartData;
            } 
            // 직접 차트 데이터가 있는 경우
            else {
              chartData = convertToChartData(contentObj.content);
              console.log('Converted chart data:', chartData);
            }
          }
          
          if (chartData) {
            elements.push({
              id: `chart-${Date.now()}-${index}`,
              type: 'chart',
              content: chartData.title || '차트',
              chartData: chartData,
              x,
              y,
              width,
              height,
              fontSize,
              fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
              color: theme.primary,
              fontWeight: 'normal',
              textAlign: 'center',
              zIndex: 5,
              backgroundColor: theme.secondary + '20',
              verticalAlign: 'middle'
            });
          }
          break;
        }
          
        case 'image':
          // Pixabay API를 사용한 이미지 검색 및 삽입
          if (slideKeywords && slideKeywords.length > 0) {
            try {
              // 키워드 중에서 랜덤으로 하나 선택
              const randomKeyword = slideKeywords[Math.floor(Math.random() * slideKeywords.length)];
              
              // Pixabay에서 이미지 검색
              const searchResult = await searchPixabayImages(randomKeyword, {
                imageType: 'photo',
                orientation: 'horizontal',
                perPage: 3,
                safeSearch: true
              });
              
              if (searchResult.hits && searchResult.hits.length > 0) {
                // 첫 번째 이미지 사용
                const imageUrl = getPixabayImageUrl(searchResult.hits[0], 'web');
                
                elements.push({
                  id: `image-${Date.now()}-${index}`,
                  type: 'image',
                  content: imageUrl,
                  title: `Image: ${randomKeyword}`,
                  x,
                  y,
                  width,
                  height,
                  fontSize,
                  fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                  color: theme.primary,
                  fontWeight: 'normal',
                  textAlign: 'center',
                  zIndex: 5,
                  backgroundColor: theme.secondary + '20',
                  verticalAlign: 'middle'
                });
              }
            } catch (error) {
              console.error('Failed to fetch image from Pixabay:', error);
              // Pixabay 실패 시 placeholder 이미지 사용
              elements.push({
                id: `image-${Date.now()}-${index}`,
                type: 'image',
                content: `https://picsum.photos/${Math.floor(aspectRatio.width * 0.8)}/${Math.floor(aspectRatio.height * 0.8)}?random=${Date.now()}`,
                title: 'Placeholder Image',
                x,
                y,
                width,
                height,
                fontSize,
                fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
                color: theme.primary,
                fontWeight: 'normal',
                textAlign: 'center',
                zIndex: 5,
                backgroundColor: theme.secondary + '20',
                verticalAlign: 'middle'
              });
            }
          }
          break;
          
        case 'container': {
          // container 타입 처리
          const containerContent = contentObj.content;
          if (containerContent && typeof containerContent === 'object' && 'items' in containerContent) {
            const containerItems = containerContent.items as Array<{
              icon: string;
              title?: string;
              content: string;
            }>;
            
            const containerLayout = containerContent.layout || contentObj.designStyle || 'icon-grid';
            
            elements.push({
              id: `container-${Date.now()}-${index}`,
              type: 'container',
              content: JSON.stringify(containerItems), // 임시로 JSON 문자열로 저장
              containerLayout: containerLayout as 'icon-grid' | 'header-list' | 'card-grid',
              containerItems: containerItems,
              containerColumns: 'auto', // 동적 레이아웃에 따라 자동 결정
              x,
              y,
              width,
              height,
              fontSize,
              fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
              color: theme.secondary,
              fontWeight: 'normal',
              textAlign: 'left',
              zIndex: 5,
              backgroundColor: theme.primary + '10',
              borderRadius: 8,
              padding: 16
            });
          }
          break;
        }
          
        default: {
          // 텍스트 요소 (기본)
          const textContent = typeof contentObj.content === 'string' ? 
            contentObj.content : 
            contentObj.content?.items ? 
              contentObj.content.items.map((item: { emoji?: string; heading?: string; description?: string }) => 
                `${item.emoji || ''} ${item.heading || ''}\n${item.description || ''}`.trim()
              ).join('\n\n') :
            safeStringify(contentObj.content || '');
              
          if (textContent.trim()) {
            elements.push({
              id: `text-${Date.now()}-${index}`,
              type: 'text',
              content: textContent,
              x,
              y,
              width,
              height,
              fontSize,
              fontFamily: themeFont?.fontFamily || 'Arial, sans-serif',
              color: theme.secondary,
              fontWeight: 'normal',
              textAlign: 'left',
              zIndex: 5,
              backgroundColor: theme.secondary + '20',
              verticalAlign: 'middle'
            });
          }
        }
      }
    }
  }
  
  return elements;
};

// 개선된 마크다운 렌더러 (리치 디자인 요소 포함)
const renderMarkdown = (text: string): string => {
  let processed = text
    // HTML 엔티티 디코딩
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // 헤딩 처리 (이모지와 색상 추가) - 이모지가 이미 있는지 체크
    .replace(/### (?!.*[🔸🔹🎯💡📌✨🚀📊💬📋✅⚡🔍📝🎨🔔🌟])(.*?)$/gmu, '<div style="display: flex; align-items: center; margin: 12px 0;"><span style="color: #00356B; margin-right: 8px;">🔸</span><h3 style="font-size: 1.1em; font-weight: 600; margin: 0; color: #00356B;">$1</h3></div>')
    .replace(/### (.*?)$/gm, '<div style="display: flex; align-items: center; margin: 12px 0;"><h3 style="font-size: 1.1em; font-weight: 600; margin: 0; color: #00356B;">$1</h3></div>')
    .replace(/## (?!.*[🔸🔹🎯💡📌✨🚀📊💬📋✅⚡🔍📝🎨🔔🌟])(.*?)$/gmu, '<div style="display: flex; align-items: center; margin: 14px 0;"><span style="color: #00356B; margin-right: 8px;">🔹</span><h2 style="font-size: 1.3em; font-weight: 600; margin: 0; color: #00356B;">$1</h2></div>')
    .replace(/## (.*?)$/gm, '<div style="display: flex; align-items: center; margin: 14px 0;"><h2 style="font-size: 1.3em; font-weight: 600; margin: 0; color: #00356B;">$1</h2></div>')
    .replace(/# (?!.*[🔸🔹🎯💡📌✨🚀📊💬📋✅⚡🔍📝🎨🔔🌟])(.*?)$/gmu, '<div style="display: flex; align-items: center; margin: 16px 0;"><span style="color: #A51C30; margin-right: 8px;">🎯</span><h1 style="font-size: 1.5em; font-weight: 700; margin: 0; color: #00356B;">$1</h1></div>')
    .replace(/# (.*?)$/gm, '<div style="display: flex; align-items: center; margin: 16px 0;"><h1 style="font-size: 1.5em; font-weight: 700; margin: 0; color: #00356B;">$1</h1></div>')
    // 볼드 처리 (배경색과 패딩 추가)
    .replace(/\*\*(.*?)\*\*/g, '<strong style="background: linear-gradient(120deg, #00356B15 0%, #A51C3015 100%); padding: 2px 6px; border-radius: 4px; color: #00356B;">$1</strong>')
    .replace(/__(.*?)__/g, '<strong style="background: linear-gradient(120deg, #00356B15 0%, #A51C3015 100%); padding: 2px 6px; border-radius: 4px; color: #00356B;">$1</strong>')
    // 이탤릭 처리
    .replace(/\*(.*?)\*/g, '<em style="color: #2c5282; font-style: italic;">$1</em>')
    .replace(/_(.*?)_/g, '<em style="color: #2c5282; font-style: italic;">$1</em>')
    // 링크 처리 (버튼 스타일) - 먼저 처리
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" style="background: #00356B; color: white; padding: 4px 8px; border-radius: 4px; text-decoration: none; display: inline-block; margin: 2px;">🔗 $1</a>')
    // 코드 처리 (박스 스타일) - 백틱과 단독 대괄호 지원
    .replace(/`(.*?)`/g, '<code style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 3px 6px; border-radius: 4px; font-family: monospace; color: #1a365d;">$1</code>')
    .replace(/\[([^\]]+)\](?!\()/g, '<code style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 3px 6px; border-radius: 4px; font-family: monospace; color: #1a365d;">$1</code>')
    // 불릿 포인트 처리 (다양한 이모지와 스타일) - 이미 이모지가 있으면 그대로 사용
    .replace(/^\* (?![💡📌✨🎯🔸🔹✅⚡📝🚀📊💬📋🔍🎨🔔🌟])(.*?)$/gmu, '<div style="display: flex; align-items: flex-start; margin: 6px 0; padding: 8px 12px; background: rgba(235, 244, 255, 0.6); border-radius: 6px; border-left: 3px solid #00356B;"><span style="color: #A51C30; margin-right: 8px; font-size: 1.1em;">💡</span><span>$1</span></div>')
    .replace(/^\* (.*?)$/gm, '<div style="display: flex; align-items: flex-start; margin: 6px 0; padding: 8px 12px; background: rgba(235, 244, 255, 0.6); border-radius: 6px; border-left: 3px solid #00356B;">$1</div>')
    .replace(/^- (?![💡📌✨🎯🔸🔹✅⚡📝🚀📊💬📋🔍🎨🔔🌟])(.*?)$/gmu, '<div style="display: flex; align-items: flex-start; margin: 6px 0; padding: 8px 12px; background: rgba(235, 244, 255, 0.6); border-radius: 6px; border-left: 3px solid #2c5282;"><span style="color: #2c5282; margin-right: 8px; font-size: 1.1em;">📌</span><span>$1</span></div>')
    .replace(/^- (.*?)$/gm, '<div style="display: flex; align-items: flex-start; margin: 6px 0; padding: 8px 12px; background: rgba(235, 244, 255, 0.6); border-radius: 6px; border-left: 3px solid #2c5282;">$1</div>')
    .replace(/^\+ (?![💡📌✨🎯🔸🔹✅⚡📝🚀📊💬📋🔍🎨🔔🌟])(.*?)$/gmu, '<div style="display: flex; align-items: flex-start; margin: 6px 0; padding: 8px 12px; background: rgba(235, 244, 255, 0.6); border-radius: 6px; border-left: 3px solid #A51C30;"><span style="color: #A51C30; margin-right: 8px; font-size: 1.1em;">✨</span><span>$1</span></div>')
    .replace(/^\+ (.*?)$/gm, '<div style="display: flex; align-items: flex-start; margin: 6px 0; padding: 8px 12px; background: rgba(235, 244, 255, 0.6); border-radius: 6px; border-left: 3px solid #A51C30;">$1</div>')
    // 번호 목록 처리 (카드 스타일)
    .replace(/^(\d+)\. (.*?)$/gm, '<div style="display: flex; align-items: flex-start; margin: 10px 0; padding: 12px 16px; background: linear-gradient(135deg, rgba(235, 244, 255, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%); border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"><div style="background: #00356B; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 0.9em; margin-right: 12px; flex-shrink: 0;">$1</div><span style="flex: 1;">$2</span></div>')
    // 인용문 처리
    .replace(/^> (.*?)$/gm, '<blockquote style="margin: 12px 0; padding: 12px 16px; background: linear-gradient(135deg, #f8fafc 0%, #ebf8ff 100%); border-left: 4px solid #A51C30; border-radius: 0 8px 8px 0; font-style: italic; color: #2d3748;"><span style="color: #A51C30; margin-right: 8px;">💬</span>$1</blockquote>')
    // 구분선 처리
    .replace(/^---$/gm, '<hr style="border: none; height: 2px; background: linear-gradient(90deg, #00356B, #2c5282, #A51C30); margin: 20px 0; border-radius: 1px;">')
    // 줄바꿈 처리
    .replace(/\n/g, '<br>');
    
  
  // 칼럼 레이아웃 감지 및 처리
  // ||| 구분자로 칼럼을 나누는 패턴 감지
  if (processed.includes('|||')) {
    const columns = processed.split('|||').map(col => col.trim());
    processed = `<div style="display: grid; grid-template-columns: repeat(${columns.length}, 1fr); gap: 20px;">
      ${columns.map(col => `<div style="padding: 12px; background: rgba(255, 255, 255, 0.8); border-radius: 8px; border-left: 4px solid #00356B; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">${col}</div>`).join('')}
    </div>`;
  }
  
  // [COL2] ... [/COL2] 또는 [COL3] ... [/COL3] 패턴 감지
  processed = processed
    .replace(/\[COL2\](.*?)\[\/COL2\]/gs, (match, content) => {
      const cols = content.split('[SPLIT]').map(col => col.trim());
      return `<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">
        ${cols.map(col => `<div style="padding: 12px; background: rgba(235, 244, 255, 0.8); border-radius: 8px; border-left: 4px solid #2c5282;">${col}</div>`).join('')}
      </div>`;
    })
    .replace(/\[COL3\](.*?)\[\/COL3\]/gs, (match, content) => {
      const cols = content.split('[SPLIT]').map(col => col.trim());
      return `<div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;">
        ${cols.map(col => `<div style="padding: 10px; background: rgba(235, 244, 255, 0.8); border-radius: 6px; border-left: 3px solid #A51C30;">${col}</div>`).join('')}
      </div>`;
    });
  
  // 프로세스 박스 [PROCESS] ... [/PROCESS]
  processed = processed.replace(/\[PROCESS\](.*?)\[\/PROCESS\]/gs, (match, content) => {
    const steps = content.split('\n').filter(step => step.trim()).map((step, i) => {
      return `<div style="display: flex; align-items: center; margin: 10px 0; padding: 12px 16px; background: linear-gradient(135deg, rgba(0, 53, 107, 0.1) 0%, rgba(165, 28, 48, 0.1) 100%); border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
        <div style="background: #00356B; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 12px;">${i + 1}</div>
        <span style="flex: 1;">${step.trim()}</span>
      </div>`;
    }).join('');
    return `<div style="margin: 16px 0;">${steps}</div>`;
  });
  
  // 하이라이트 박스 [HIGHLIGHT] ... [/HIGHLIGHT]
  processed = processed.replace(/\[HIGHLIGHT\](.*?)\[\/HIGHLIGHT\]/gs, '<div style="background: linear-gradient(135deg, #ebf8ff 0%, #f8fafc 100%); border: 2px solid #00356B; border-radius: 12px; padding: 16px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">$1</div>');
  
  return processed;
};

// 불필요한 목록 래핑 함수 (이제 renderMarkdown에서 단순 처리)
const wrapLists = (html: string): string => {
  // 단순히 HTML을 반환 (불릿 포인트는 이미 • 문자로 처리됨)
  return html;
};

const SLIDE_LAYOUTS = [
  'title-center',
  'title-top-content-bottom',
  'title-left-content-right',
  'title-right-content-left',
  'title-only',
  'title-small-top-left',
  'title-small-top-right'
];

// 슬라이드별 자동 테마 선택 함수
const getRandomSlideTheme = (slideIndex: number): 'default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'image-card' | 'diagonal' => {
  const themes: ('default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'image-card' | 'diagonal')[] = [
    'default', 'left-split', 'right-split', 'top-split', 'bottom-split', 'image-card', 'diagonal'
  ];
  
  // 첫 번째 슬라이드는 항상 기본형
  if (slideIndex === 0) {
    return 'default';
  }
  
  // 나머지 슬라이드는 순환하며 다양한 테마 적용
  return themes[slideIndex % themes.length];
};

// 분할 유형 자동 선택 함수
const getRandomDividerType = (slideIndex: number): 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient' => {
  const dividers: ('straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient')[] = [
    'straight', 'convex', 'concave', 'zigzag', 'jagged', 'gradient'
  ];
  
  // 기본형이나 image-card는 분할선이 없으므로 straight 사용
  return dividers[slideIndex % dividers.length];
};

// 기본 제목/내용 요소 생성 함수

// 토큰 사용량과 함께 슬라이드를 생성하는 함수
export const generateSlidesWithUsage = async (
  content: string,
  theme: Theme,
  aspectRatio: AspectRatio,
  apiKey: string,
  language: 'ko' | 'en',
  slideType: SlideType = 'ppt',
  slideCount = 5,
  templateStyle?: string,
  slideTheme: 'default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'image-card' | 'diagonal' = 'default',
  dividerType: 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient' = 'straight'
): Promise<{ slides: Slide[]; tokenUsage: TokenUsage | null }> => {
  try {
    const result = await generateSlidesWithGeminiAndUsage(content, apiKey, language, slideType, slideCount);
    
    const slides: Slide[] = await Promise.all(result.slides.map(async (slide, index) => {
      const layout = getLayoutForSlideType(slideType, index, templateStyle);
      
      // 슬라이드별 자동 테마 및 분할 유형 적용
      const autoSlideTheme = getRandomSlideTheme(index);
      const autoDividerType = autoSlideTheme === 'default' || autoSlideTheme === 'image-card' ? 'straight' : getRandomDividerType(index);
      
      const backgroundOptions = createDefaultBackgroundOptions(slide.title);
      // Issue #1 fix: Ensure first slide always gets background
      const backgroundImage = generatePicsumImage(
        aspectRatio.width,
        aspectRatio.height,
        backgroundOptions.seed,
        backgroundOptions.blur,
        backgroundOptions.grayscale
      );
      
      let slideElements: SlideElement[] = [];
      
      // 슬라이드 테마 결정 (사용자가 선택한 테마가 있으면 그것을 사용, 아니면 자동 테마 사용)
      const finalSlideTheme = slideTheme === 'default' ? autoSlideTheme : slideTheme;
      
      // 구조화된 콘텐츠가 있으면 사용
      if (slide.structuredContent && slide.structuredContent.length > 0) {
        slideElements = await createStructuredElements(
          slide.structuredContent,
          theme,
          aspectRatio,
          undefined, // themeFont는 나중에 App에서 전달됨
          layout, // 레이아웃 전달
          slide.keywords // 키워드 전달
        );
        
        // 제목은 createStructuredElements에서 이미 처리됨
        
        // 랜덤 동적 레이아웃 적용 (제목 제외)
        const objectCount = countContentObjects(slideElements, aspectRatio.height);
        if (objectCount > 0) {
          const randomLayoutOption = getRandomLayoutOption(objectCount);
          // finalSlideTheme은 이미 위에서 선언됨
          const contentArea = getContentArea(aspectRatio, finalSlideTheme, 50);
          const newPositions = calculateObjectPositions(randomLayoutOption, objectCount, contentArea, aspectRatio.width, aspectRatio.height);
          
          // 제목이 아닌 요소들만 필터링하여 새 위치 적용
          const contentElements = slideElements.filter(element => 
            !isTitle(element, aspectRatio.height)
          );
          
          slideElements = slideElements.map(element => {
            // 제목 요소는 건드리지 않음
            if (isTitle(element, aspectRatio.height)) {
              return element;
            }
            
            const elementIndex = contentElements.findIndex(el => el.id === element.id);
            if (elementIndex >= 0 && elementIndex < newPositions.length) {
              const position = newPositions[elementIndex];
              return {
                ...element,
                x: position.x,
                y: position.y,
                width: position.width,
                height: position.height
              };
            }
            
            return element;
          });
        }
      }
      
      // finalSlideTheme 선언이 이미 위에서 되었으므로 여기서는 재선언하지 않음
      const finalDividerType = dividerType === 'random' ? autoDividerType : dividerType;
      
      const htmlContent = generateSlideHTML(
        slide.title, 
        slide.content, 
        theme, 
        aspectRatio, 
        index,
        undefined, // themeFont
        undefined, // borderStyle
        layout,
        backgroundImage,
        backgroundOptions.blur,
        0.3, // 기본 오버레이
        undefined, // backgroundColor
        undefined, // backgroundPattern
        slideElements, // 구조화된 요소들 전달
        slideType,
        false, // fontBorder
        false  // fontGlow
      );
      
      return {
        id: `slide-${Date.now()}-${index}`,
        title: slide.title,
        content: slide.content,
        htmlContent,
        order: index,
        keywords: slide.keywords || [],
        slideLayout: layout.id,
        elements: slideElements,
        slideTheme: finalSlideTheme,
        dividerType: finalDividerType,
        ...(index === 0 && { backgroundImage }),
      };
    }));
    
    return { slides, tokenUsage: result.tokenUsage };
  } catch (error) {
    console.error('슬라이드 생성 실패:', error);
    throw error;
  }
};

export const generateSlides = async (
  content: string,
  theme: Theme,
  aspectRatio: AspectRatio,
  apiKey: string,
  language: 'ko' | 'en',
  slideType: SlideType = 'ppt',
  slideCount = 5,
  templateStyle?: string,
  slideTheme: 'default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'image-card' | 'diagonal' = 'default',
  dividerType: 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient' = 'straight'
): Promise<Slide[]> => {
  try {
    const slideData = await generateSlidesWithGemini(content, apiKey, language, slideType, slideCount);
    
    const slides: Slide[] = await Promise.all(slideData.map(async (slide, index) => {
      const layout = getLayoutForSlideType(slideType, index, templateStyle);
      
      // 슬라이드별 자동 테마 및 분할 유형 적용
      const autoSlideTheme = getRandomSlideTheme(index);
      const autoDividerType = autoSlideTheme === 'default' || autoSlideTheme === 'image-card' ? 'straight' : getRandomDividerType(index);
      
      const backgroundOptions = createDefaultBackgroundOptions(slide.title);
      // Issue #1 fix: Ensure first slide always gets background
      const backgroundImage = generatePicsumImage(
        aspectRatio.width,
        aspectRatio.height,
        backgroundOptions.seed,
        backgroundOptions.blur,
        backgroundOptions.grayscale
      );
      
      let elements: SlideElement[] = [];
      
      // 슬라이드 테마 결정 (사용자가 선택한 테마가 있으면 그것을 사용, 아니면 자동 테마 사용)
      const finalSlideTheme = slideTheme === 'default' ? autoSlideTheme : slideTheme;
      
      // 구조화된 콘텐츠가 있으면 사용
      if (slide.structuredContent && slide.structuredContent.length > 0) {
        elements = await createStructuredElements(
          slide.structuredContent,
          theme,
          aspectRatio,
          undefined, // themeFont는 나중에 App에서 전달됨
          layout, // 레이아웃 전달
          slide.keywords // 키워드 전달
        );
        
        // 제목은 createStructuredElements에서 이미 처리됨
        
        // 랜덤 동적 레이아웃 적용 (제목 제외)
        const objectCount = countContentObjects(elements, aspectRatio.height);
        if (objectCount > 0) {
          const randomLayoutOption = getRandomLayoutOption(objectCount);
          // finalSlideTheme은 이미 위에서 선언됨
          const contentArea = getContentArea(aspectRatio, finalSlideTheme, 50);
          const newPositions = calculateObjectPositions(randomLayoutOption, objectCount, contentArea, aspectRatio.width, aspectRatio.height);
          
          // 제목이 아닌 요소들만 필터링하여 새 위치 적용
          const contentElements = elements.filter(element => 
            !isTitle(element, aspectRatio.height)
          );
          
          elements = elements.map(element => {
            // 제목 요소는 건드리지 않음
            if (isTitle(element, aspectRatio.height)) {
              return element;
            }
            
            const elementIndex = contentElements.findIndex(el => el.id === element.id);
            if (elementIndex >= 0 && elementIndex < newPositions.length) {
              const position = newPositions[elementIndex];
              return {
                ...element,
                x: position.x,
                y: position.y,
                width: position.width,
                height: position.height
              };
            }
            
            return element;
          });
        }
      }
      
      const htmlContent = generateSlideHTML(
        slide.title, 
        slide.content, 
        theme, 
        aspectRatio, 
        index,
        undefined, // themeFont
        undefined, // borderStyle
        layout,
        backgroundImage,
        backgroundOptions.blur,
        0.3, // 기본 오버레이
        undefined, // backgroundColor
        undefined, // backgroundPattern
        elements, // 구조화된 요소들 전달
        slideType,
        false, // fontBorder
        false  // fontGlow
      );
      
      return {
        id: `slide-${Date.now()}-${index}`,
        title: slide.title,
        content: slide.content,
        htmlContent,
        order: index,
        keywords: slide.keywords || [],
        template: layout,
        backgroundImage,
        backgroundBlur: backgroundOptions.blur,
        backgroundSeed: backgroundOptions.seed,
        backgroundGrayscale: backgroundOptions.grayscale,
        backgroundType: 'image', // 첫 슬라이드 배경 타입 명시
        backgroundColor: undefined,
        slideLayout: 'title-top-content-bottom',
        themeOverlay: 0.3,
        slideTheme: finalSlideTheme,
        dividerType: dividerType === 'random' ? autoDividerType : dividerType,
        elements: elements,
        history: [],
        historyIndex: 0,
        aspectRatio,
        // 테마 색상 정보 추가
        themeColors: {
          primary: theme.primary,
          secondary: theme.secondary,
          accent: theme.accent,
          quaternary: theme.quaternary
        }
      };
    }));

    return slides;
  } catch {  // Removed unused error variable
    // console.error('Failed to generate slides with AI:', error);
    // Fallback to simple parsing if API fails
    return generateSlidesSimple(content, theme, aspectRatio, slideType, templateStyle);
  }
};

const getLayoutForSlideType = (slideType: SlideType, index: number, templateStyle?: string): string => {
  // 템플릿 스타일이 지정된 경우
  if (templateStyle && templateStyle !== 'mixed-auto') {
    switch (templateStyle) {
      case 'presentation-formal':
        return 'title-top-content-bottom';
      case 'title-focus':
        return 'title-only';
      case 'content-heavy':
        return 'title-small-top-left';
      default:
        return 'title-top-content-bottom';
    }
  }

  // 자동 혼합 모드이거나 템플릿이 지정되지 않은 경우
  switch (slideType) {
    case 'cardnews':
      return 'title-center'; 
    case 'imagecard':
      return 'title-center';
    case 'ppt':
    default: {
      // 첫 번째 슬라이드는 제목 중심
      if (index === 0) {
        return 'title-only';
      }
      // 이후 슬라이드는 제목 중심형을 제외한 레이아웃 중 랜덤 선택
      const availableLayouts = SLIDE_LAYOUTS.filter(layout => layout !== 'title-only');
      return availableLayouts[Math.floor(Math.random() * availableLayouts.length)];
    }
  }
};

const generateSlidesSimple = (
  content: string,
  theme: Theme,
  aspectRatio: AspectRatio,
  slideType: SlideType,
  templateStyle?: string
): Slide[] => {
  // 섹션 분할
  const sections = content.split(/\n\s*\n/).filter(section => section.trim());
  
  // 자르기
  const slides: Slide[] = [];
  
  sections.forEach((section, sectionIndex) => {
    const lines = section.split('\n').filter(line => line.trim());
    const wordsCount = section.split(' ').length;
    
    // 살라이드 타입
    const maxWords = slideType === 'cardnews' ? 30 : slideType === 'imagecard' ? 50 : 100;
    
    // 너무 길 때 자르기
    if (wordsCount > maxWords) {
      const sentences = section.split(/[.!?]+/).filter(s => s.trim());
      let currentSlideContent = '';
      let slideCount = 0;
      
      sentences.forEach((sentence) => {
        const maxWordsPerSlide = slideType === 'cardnews' ? 20 : slideType === 'imagecard' ? 40 : 80;
        
        if (currentSlideContent.split(' ').length + sentence.split(' ').length > maxWordsPerSlide) {
          // 슬라이드 만들기
          const title = lines[0] || `${sectionIndex + 1}.${slideCount + 1} 주요 내용`;
          const layout = getLayoutForSlideType(slideType, slides.length, templateStyle);
          const backgroundImage = getBackgroundForContent(currentSlideContent);
          const htmlContent = generateSlideHTML(title, currentSlideContent.trim(), theme, aspectRatio, slides.length, undefined, undefined, layout, backgroundImage, 2, 0.3, undefined, undefined, undefined, slideType, false, false);
          
          slides.push({
            id: `slide-${Date.now()}-${slides.length}`,
            title,
            content: currentSlideContent.trim(),
            htmlContent,
            order: slides.length,
            template: layout,
            backgroundImage,
            backgroundBlur: 2,
            backgroundType: 'image',
            backgroundColor: undefined,
            themeOverlay: 0.3,
            elements: [],
            history: [],
            historyIndex: 0,
            aspectRatio,
          });
          
          currentSlideContent = sentence.trim() + '.';
          slideCount++;
        } else {
          currentSlideContent += (currentSlideContent ? ' ' : '') + sentence.trim() + '.';
        }
      });
      
      // 마지막 슬라이드 처리
      if (currentSlideContent.trim()) {
        const title = lines[0] || `${sectionIndex + 1}.${slideCount + 1} 주요 내용`;
        const layout = getLayoutForSlideType(slideType, slides.length, templateStyle);
        const backgroundImage = getBackgroundForContent(currentSlideContent);
        const htmlContent = generateSlideHTML(title, currentSlideContent.trim(), theme, aspectRatio, slides.length, undefined, undefined, layout, backgroundImage, 2, 0.3, undefined, undefined, undefined, slideType, false, false);
        
        slides.push({
          id: `slide-${Date.now()}-${slides.length}`,
          title,
          content: currentSlideContent.trim(),
          htmlContent,
          order: slides.length,
          template: layout,
          backgroundImage,
          backgroundBlur: 2,
          backgroundType: 'image',
          backgroundColor: undefined,
          themeOverlay: 0.3,
          elements: [],
          history: [],
          historyIndex: 0,
          aspectRatio,
        });
      }
    } else {
      // 일반적인 슬라이드 생성
      const title = lines[0] || `슬라이드 ${slides.length + 1}`;
      const slideContent = lines.slice(1).join('\n') || section;
      const layout = getLayoutForSlideType(slideType, slides.length, templateStyle);
      const backgroundImage = getBackgroundForContent(slideContent);
      const htmlContent = generateSlideHTML(title, slideContent, theme, aspectRatio, slides.length, undefined, undefined, layout, backgroundImage, 2, 0.3, undefined, undefined, undefined, slideType, false, false);
      
      slides.push({
        id: `slide-${Date.now()}-${slides.length}`,
        title,
        content: slideContent,
        htmlContent,
        order: slides.length,
        template: layout,
        backgroundImage,
        backgroundBlur: 2,
        backgroundType: 'image',
        backgroundColor: undefined,
        themeOverlay: 0.3,
        elements: [],
        history: [],
        historyIndex: 0,
        aspectRatio,
      });
    }
  });

  return slides.length > 0 ? slides : [{
    id: `slide-${Date.now()}-0`,
    title: '기본 슬라이드',
    content: content,
    htmlContent: generateSlideHTML('기본 슬라이드', content, theme, aspectRatio, 0, undefined, undefined, 'title-center', getBackgroundForContent(content), 2, 0.3, undefined, undefined, undefined, slideType),
    order: 0,
    template: 'title-center',
    backgroundImage: getBackgroundForContent(content),
    backgroundBlur: 2,
    backgroundType: 'image',
    backgroundColor: undefined,
    themeOverlay: 0.3,
    elements: [],
    history: [],
    historyIndex: 0,
    aspectRatio,
  }];
};

// ECharts 옵션 생성 함수
// Memoized chart option generation
const chartOptionCache = new WeakMap<ChartData, Map<string, EChartsOption>>();

const getChartOption = (chartData: ChartData, theme: Theme): EChartsOption => {
  // Check cache first
  const themeKey = JSON.stringify(theme);
  if (chartOptionCache.has(chartData)) {
    const themeCache = chartOptionCache.get(chartData)!;
    if (themeCache.has(themeKey)) {
      return themeCache.get(themeKey)!;
    }
  }
  const baseOption: EChartsOption = {
    title: {
      text: chartData.title || '',
      textStyle: {
        color: theme.primary || '#333333',
        fontSize: 16
      }
    },
    tooltip: {
      trigger: chartData.type === 'pie' ? 'item' : 'axis'
    },
    legend: {
      data: chartData.data.series?.map(s => s.name) || chartData.data.labels,
      textStyle: {
        color: theme.secondary || '#666666'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    }
  };

  switch (chartData.type) {
    case 'line':
      return {
        ...baseOption,
        xAxis: {
          type: 'category',
          data: chartData.data.labels,
          axisLabel: { color: theme.secondary || '#666666' }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: theme.secondary || '#666666' }
        },
        series: chartData.data.series ? chartData.data.series.map(s => ({
          name: s.name,
          type: 'line',
          data: s.data,
          smooth: true
        })) : [{
          type: 'line',
          data: chartData.data.values,
          smooth: true
        }]
      };

    case 'bar':
      return {
        ...baseOption,
        xAxis: {
          type: 'category',
          data: chartData.data.labels,
          axisLabel: { color: theme.secondary || '#666666' }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: theme.secondary || '#666666' }
        },
        series: chartData.data.series ? chartData.data.series.map(s => ({
          name: s.name,
          type: 'bar',
          data: s.data
        })) : [{
          type: 'bar',
          data: chartData.data.values,
          itemStyle: {
            borderRadius: [4, 4, 0, 0]
          }
        }]
      };

    case 'pie':
      return {
        ...baseOption,
        series: [{
          type: 'pie',
          radius: '50%',
          data: chartData.data.labels.map((label, index) => ({
            value: Array.isArray(chartData.data.values[0]) ? 
              chartData.data.values[0][index] : 
              chartData.data.values[index],
            name: label
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };

    case 'scatter':
      return {
        ...baseOption,
        xAxis: {
          type: 'value',
          axisLabel: { color: theme.secondary || '#666666' }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: theme.secondary || '#666666' }
        },
        series: [{
          type: 'scatter',
          data: chartData.data.values,
          symbolSize: 10
        }]
      };

    case 'radar':
      return {
        ...baseOption,
        radar: {
          indicator: chartData.data.labels.map(label => ({ name: label })),
          axisName: { color: theme.secondary || '#666666' }
        },
        series: [{
          type: 'radar',
          data: [{
            value: chartData.data.values,
            name: chartData.title || 'Data'
          }]
        }]
      };

    case 'heatmap':
      return {
        ...baseOption,
        xAxis: {
          type: 'category',
          data: chartData.data.labels,
          axisLabel: { color: theme.secondary || '#666666' }
        },
        yAxis: {
          type: 'category',
          data: chartData.data.labels,
          axisLabel: { color: theme.secondary || '#666666' }
        },
        visualMap: {
          min: 0,
          max: 100,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          bottom: '15%',
          textStyle: { color: theme.secondary || '#666666' }
        },
        series: [{
          type: 'heatmap',
          data: chartData.data.values,
          label: { show: true }
        }]
      };

    case 'treemap':
      return {
        ...baseOption,
        series: [{
          type: 'treemap',
          data: chartData.data.labels.map((label, index) => ({
            name: label,
            value: Array.isArray(chartData.data.values[0]) ? 
              chartData.data.values[0][index] : 
              chartData.data.values[index]
          }))
        }]
      };

    default:
      // 기본값은 bar 차트
      return {
        ...baseOption,
        xAxis: {
          type: 'category',
          data: chartData.data.labels
        },
        yAxis: {
          type: 'value'
        },
        series: [{
          type: 'bar',
          data: chartData.data.values
        }]
      };
  }
};

export const generateSlideHTML = (
  title: string,
  content: string,
  theme: Theme,
  aspectRatio: AspectRatio,
  slideIndex: number,
  themeFont?: ThemeFont,
  borderStyle?: SlideBorderStyle,
  layout = 'title-center',
  backgroundImage?: string,
  backgroundBlur = 2,
  themeOverlay = 0.3,
  backgroundColor?: string,
  backgroundPattern?: string,
  elements?: SlideElement[],
  slideType?: string,
  fontBorder?: boolean,
  fontGlow?: boolean,
  slideTheme: 'default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'image-card' | 'diagonal' = 'default',
  dividerType: 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient' = 'straight',
  splitPosition?: number
): string => {
  // 콘텐츠 분량 계산 (함수 외부로 이동)
  const totalContentLength = title.length + content.length;
  
  // 슬라이드 타입과 화면 비율에 따른 동적 폰트 크기 계산 (개선된 반응형)
  const getResponsiveFontSizes = () => {
    // 종횡비에 따른 기준 크기 계산 (너비와 높이 모두 고려)
    const aspectRatioValue = aspectRatio.width / aspectRatio.height;
    const baseWidth = 1920; // 표준 기준 너비 (16:9 기준)
    const baseHeight = 1080; // 표준 기준 높이
    
    // 현재 슬라이드 크기에 대한 스케일링 팩터 계산
    const widthScale = aspectRatio.width / baseWidth;
    const heightScale = aspectRatio.height / baseHeight;
    
    // 종횡비별 최적화된 스케일링
    let scaleFactor;
    if (aspectRatioValue >= 2.0) { // 21:9 같은 초광대형
      scaleFactor = Math.min(widthScale * 0.9, heightScale * 1.1);
    } else if (aspectRatioValue >= 1.6) { // 16:9, 16:10 광각형
      scaleFactor = Math.min(widthScale, heightScale);
    } else if (aspectRatioValue >= 1.2) { // 4:3 표준형
      scaleFactor = Math.min(widthScale * 1.1, heightScale * 0.9);
    } else { // 1:1, 9:16 정사각/세로형
      scaleFactor = Math.min(widthScale * 1.2, heightScale * 0.8);
    }
    
    // 최소/최대 제한 (더 넓은 범위로 조정)
    scaleFactor = Math.max(0.5, Math.min(scaleFactor, 2.0));
    
    // 콘텐츠 분량 기반 폰트 크기 조정
    const getContentLengthMultiplier = () => {
      // 모든 타입에서 콘텐츠 분량에 따른 폰트 크기 조정
      if (totalContentLength > 500) {
        return 0.7; // 매우 긴 내용 - 작은 폰트
      } else if (totalContentLength > 300) {
        return 0.8; // 긴 내용 - 작은 폰트
      } else if (totalContentLength > 150) {
        return 0.9; // 보통 내용 - 기본보다 약간 작은 폰트
      } else if (totalContentLength > 80) {
        return 1.0; // 적당한 내용 - 기본 폰트
      } else {
        return 1.1; // 짧은 내용 - 약간 큰 폰트
      }
    };
    
    const contentLengthMultiplier = getContentLengthMultiplier();
    
    // 슬라이드 타입별 기본 크기 조정 (더 보수적으로)
    let titleMultiplier = 2.0; // 기본값 줄임
    let contentMultiplier = 1.0; // 기본값 줄임
    
    switch (slideType) {
      case 'cardnews':
        // 카드뉴스: 큰 제목, 중간 내용
        titleMultiplier = 2.5 * contentLengthMultiplier;
        contentMultiplier = 1.3 * contentLengthMultiplier;
        break;
      case 'imagecard':
        // 이미지카드: 큰 제목, 작은 내용
        titleMultiplier = 3.0 * contentLengthMultiplier;
        contentMultiplier = 1.1 * contentLengthMultiplier;
        break;
      case 'ppt':
      default:
        // PPT: 표준 크기
        titleMultiplier = 2.0 * contentLengthMultiplier;
        contentMultiplier = 1.0 * contentLengthMultiplier;
        break;
    }
    
    // 화면 기준 글씨 크기 계산 (12자 제목, 30자 내용 기준)
    const slideWidth = aspectRatio.width;
    
    // 제목: 한글 12자 기준으로 폰트 크기 계산
    const titleFontSize = Math.max(12, slideWidth / 12 / 2.5); // 한글자당 약 2.5배수 고려
    
    // 내용: 한글 30자 기준으로 폰트 크기 계산  
    const contentFontSize = Math.max(10, slideWidth / 30 / 1.8); // 한글자당 약 1.8배수 고려
    
    // 콘텐츠 분량에 따른 추가 조정
    const titleAdjustment = totalContentLength > 200 ? 0.9 : totalContentLength < 100 ? 1.1 : 1.0;
    const contentAdjustment = totalContentLength > 500 ? 0.8 : totalContentLength < 150 ? 1.0 : 0.9;
    
    return {
      title: Math.min(titleFontSize * titleAdjustment, 40), // 최대 40px
      subtitle: Math.min(titleFontSize * 0.7 * titleAdjustment, 30), // 최대 30px
      content: Math.min(contentFontSize * contentAdjustment, 20), // 최대 20px
      bullet: Math.min(contentFontSize * 0.9 * contentAdjustment, 18), // 최대 18px
    };
  };

  const fontSizes = getResponsiveFontSizes();
  
  // 구조화된 요소가 있는지 확인
  // 모든 요소는 elements 배열에서만 관리 (기본 요소 생성 제거)
  const allElements = elements || [];

  const getLayoutStyles = () => {
    switch (layout) {
      case 'title-top-content-bottom':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-center',
          contentPosition: 'text-center',
          flexDirection: 'flex-col',
          titleWidth: '100%',
          contentWidth: '100%',
          gap: '2rem',
          padding: '2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'title-left-content-right':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-left',
          contentPosition: 'text-left',
          flexDirection: 'flex-row',
          titleWidth: '45%',
          contentWidth: '50%',
          gap: '5%',
          padding: '2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'title-right-content-left':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-right',
          contentPosition: 'text-left',
          flexDirection: 'flex-row-reverse',
          titleWidth: '45%',
          contentWidth: '50%',
          gap: '5%',
          padding: '2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'title-only':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-center',
          contentPosition: 'text-center',
          flexDirection: 'flex-col',
          titleWidth: '100%',
          contentWidth: '0%',
          gap: '0',
          padding: '2rem',
          titleSize: fontSizes.title * 1.3,
          contentSize: 0
        };
      case 'title-small-top-left':
        return {
          container: 'justify-start items-start',
          titlePosition: 'text-left',
          contentPosition: 'text-left',
          flexDirection: 'flex-col',
          titleWidth: '100%',
          contentWidth: '100%',
          gap: '1.5rem',
          padding: '2rem',
          titleSize: fontSizes.title * 0.6,
          contentSize: fontSizes.content * 1.2
        };
      case 'title-small-top-right':
        return {
          container: 'justify-start items-end',
          titlePosition: 'text-right',
          contentPosition: 'text-left',
          flexDirection: 'flex-col',
          titleWidth: '100%',
          contentWidth: '100%',
          gap: '1.5rem',
          padding: '2rem',
          titleSize: fontSizes.title * 0.6,
          contentSize: fontSizes.content * 1.2
        };
      case 'title-left':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-left',
          contentPosition: 'text-left',
          flexDirection: 'flex-row',
          titleWidth: '40%',
          contentWidth: '55%',
          gap: '5%',
          padding: '2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'title-right':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-right',
          contentPosition: 'text-left',
          flexDirection: 'flex-row-reverse',
          titleWidth: '40%',
          contentWidth: '55%',
          gap: '5%',
          padding: '2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'title-top':
        return {
          container: 'justify-start items-center',
          titlePosition: 'text-center',
          contentPosition: 'text-center',
          flexDirection: 'flex-col',
          titleWidth: '100%',
          contentWidth: '100%',
          gap: '1.5rem',
          padding: '2.5rem 2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'title-bottom':
        return {
          container: 'justify-end items-center',
          titlePosition: 'text-center',
          contentPosition: 'text-center',
          flexDirection: 'flex-col-reverse',
          titleWidth: '100%',
          contentWidth: '100%',
          gap: '1.5rem',
          padding: '2.5rem 2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      case 'split-content':
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-center',
          contentPosition: 'text-left',
          flexDirection: 'flex-row',
          titleWidth: '45%',
          contentWidth: '45%',
          gap: '10%',
          padding: '2rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
      default: // title-center
        return {
          container: 'justify-center items-center',
          titlePosition: 'text-center',
          contentPosition: 'text-center',
          flexDirection: 'flex-col',
          titleWidth: '100%',
          contentWidth: '100%',
          gap: '1.8rem',
          padding: '2.5rem',
          titleSize: fontSizes.title,
          contentSize: fontSizes.content
        };
    }
  };

  const layoutStyles = getLayoutStyles();
  
  // 마크다운 렌더링은 이제 개별 요소에서 처리됨
  
  // 테마 색상 인덱스 적용
  const themeColors = getThemeColors(theme);
  
  // 폰트 스타일 적용
  const getFontStyles = () => {
    let textShadow = 'none';
    let webkitTextStroke = 'none';
    let paintOrder = 'normal';
    
    if (themeFont) {
      textShadow = themeFont.effects.textShadow || 'none';
    }
    
    // 폰트 빛나는 효과
    if (fontGlow) {
      textShadow = `0 0 20px ${themeColors.accent}, 0 0 30px ${themeColors.accent}, 0 0 40px ${themeColors.accent}`;
    }
    
    // 폰트 테두리 효과
    if (fontBorder) {
      webkitTextStroke = `2px ${themeColors.border}`;
      paintOrder = 'stroke fill';
    }
    
    if (!themeFont) {return {
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      textShadow,
      letterSpacing: 'normal',
      fontWeight: '400',
      webkitTextStroke,
      paintOrder
    };}
    
    return {
      fontFamily: themeFont.fontFamily,
      textShadow,
      letterSpacing: themeFont.effects.letterSpacing || 'normal',
      fontWeight: themeFont.effects.fontWeight || '400',
      webkitTextStroke,
      paintOrder
    };
  };

  const fontStyles = getFontStyles();

  // 박스 스타일 적용 (배경색, 패딩, 테두리 포함)
  const getBoxStyles = () => {
    if (!borderStyle) {
      return {
        border: `2px solid ${themeColors.border}30`,
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        backgroundColor: 'transparent',
        padding: '2rem'
      };
    }
    
    // 테마 색상 기반 배경색 계산
    const getThemeBackgroundColor = (colorType: string) => {
      const baseColor = borderStyle.backgroundColor || 'transparent';
      
      if (baseColor === 'transparent') {return 'transparent';}
      
      // rgba(theme-primary, 0.05) 형태의 문자열을 실제 색상으로 변환
      if (baseColor.includes('theme-primary')) {
        return `${themeColors.background}0D`; // 0D = opacity 0.05
      } else if (baseColor.includes('theme-secondary')) {
        return `${themeColors.background}14`; // 14 = opacity 0.08
      } else if (baseColor.includes('theme-accent')) {
        return `${themeColors.background}1A`; // 1A = opacity 0.1
      } else if (baseColor.includes('theme-quaternary')) {
        return `${themeColors.background}0F`; // 0F = opacity 0.06
      }
      
      return baseColor;
    };
    
    return {
      border: `${borderStyle.borderWidth}px ${borderStyle.borderStyle} ${borderStyle.id === 'bold-frame' ? themeColors.accent : themeColors.border + '60'}`,
      borderRadius: `${borderStyle.borderRadius}px`,
      boxShadow: borderStyle.boxShadow,
      backgroundColor: getThemeBackgroundColor(borderStyle.backgroundColor || ''),
      padding: borderStyle.padding ? `${borderStyle.padding}px` : '2rem'
    };
  };

  const boxStyles = getBoxStyles();

  // 패턴 스타일 생성 함수
  const getPatternStyle = (pattern: string) => {
    switch (pattern) {
      case 'grid-small':
        return `background-image: 
          linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 20px 20px;`;
      case 'grid-large':
        return `background-image: 
          linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 50px 50px;`;
      case 'grid-xlarge':
        return `background-image: 
          linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 100px 100px;`;
      case 'lines-horizontal':
        return `background-image: linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 100% 30px;`;
      case 'lines-vertical':
        return `background-image: linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 30px 100%;`;
      case 'wave-large':
        return `background-image: radial-gradient(ellipse at center, rgba(0,0,0,0.1) 20%, transparent 50%);
          background-size: 100px 50px;`;
      case 'wave-small':
        return `background-image: radial-gradient(ellipse at center, rgba(0,0,0,0.08) 15%, transparent 35%);
          background-size: 40px 20px;`;
      case 'circle-fractal':
        return `background-image: 
          radial-gradient(circle at 25% 25%, rgba(0,0,0,0.1) 2px, transparent 2px),
          radial-gradient(circle at 75% 75%, rgba(0,0,0,0.1) 2px, transparent 2px);
          background-size: 30px 30px;`;
      case 'glassmorphic':
        return `backdrop-filter: blur(10px);
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);`;
      case 'mosaic':
        return `background-image: 
          linear-gradient(45deg, rgba(0,0,0,0.1) 25%, transparent 25%),
          linear-gradient(-45deg, rgba(0,0,0,0.1) 25%, transparent 25%);
          background-size: 20px 20px;
          background-position: 0 0, 10px 10px;`;
      case 'digital-mosaic':
        return `background-image: 
          linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px),
          linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 8px 8px;`;
      case 'water-drops':
        return `background-image: 
          radial-gradient(circle at 20% 20%, rgba(0,0,0,0.1) 2px, transparent 5px),
          radial-gradient(circle at 80% 60%, rgba(0,0,0,0.08) 3px, transparent 6px),
          radial-gradient(circle at 60% 80%, rgba(0,0,0,0.06) 1px, transparent 4px);
          background-size: 60px 60px, 80px 80px, 40px 40px;`;
      case 'rainbow':
        return `background-image: 
          linear-gradient(45deg, 
            rgba(255,0,0,0.1) 0%, 
            rgba(255,255,0,0.1) 17%, 
            rgba(0,255,0,0.1) 33%, 
            rgba(0,255,255,0.1) 50%, 
            rgba(0,0,255,0.1) 67%, 
            rgba(255,0,255,0.1) 83%, 
            rgba(255,0,0,0.1) 100%);
          background-size: 200px 200px;`;
      case 'gaussian-blur':
        return `filter: blur(2px); opacity: 0.9;`;
      case 'motion-blur-horizontal':
        return `filter: blur(1px); transform: scaleX(1.02);`;
      case 'motion-blur-vertical':
        return `filter: blur(1px); transform: scaleY(1.02);`;
      case 'zigzag':
        return `background-image: 
          linear-gradient(45deg, rgba(0,0,0,0.1) 12.5%, transparent 12.5%, transparent 37.5%, rgba(0,0,0,0.1) 37.5%, rgba(0,0,0,0.1) 62.5%, transparent 62.5%, transparent 87.5%, rgba(0,0,0,0.1) 87.5%);
          background-size: 30px 30px;`;
      case 'fisheye':
        return `transform: perspective(500px) rotateX(5deg); filter: brightness(1.1);`;
      case 'wide-angle':
        return `transform: perspective(1000px) rotateY(2deg) rotateX(1deg); filter: contrast(1.1);`;
      default:
        return '';
    }
  };

  // 배경 스타일 생성 함수
  const getBackgroundStyle = () => {
    let baseBackground = '';
    
    // 슬라이드 테마가 분할형인 경우 배경 이미지는 getSplitLayout에서 처리
    const isSplitTheme = slideTheme !== 'default' && slideTheme !== 'diagonal';
    
    if (backgroundColor) {
      // 컬러 배경 사용
      baseBackground = `background: ${backgroundColor};`;
    } else if (backgroundImage && !isSplitTheme) {
      // 이미지 배경 사용 (분할형 테마가 아닌 경우에만)
      const themeHex = theme.primary.replace('#', '');
      const r = parseInt(themeHex.substr(0, 2), 16);
      const g = parseInt(themeHex.substr(2, 2), 16);
      const b = parseInt(themeHex.substr(4, 2), 16);
      const themeRgba = `rgba(${r}, ${g}, ${b}, ${themeOverlay})`;
      
      baseBackground = `background: linear-gradient(${themeRgba}, ${themeRgba}), url('${backgroundImage}') center/cover;`;
    } else {
      // 기본 그라데이션 배경
      baseBackground = `background: linear-gradient(135deg, ${theme.primary}15, ${theme.secondary}15);`;
    }

    // Issue #12 fix: Pattern/filter overlay applies on top of existing backgrounds
    return baseBackground;
  };

  // slideTheme에 따라 요소 위치 조정
  const adjustElementPosition = (element: SlideElement): SlideElement => {
    if (slideTheme === 'default' || !element.x || !element.y || !element.width || !element.height) {
      return element;
    }

    const newElement = { ...element };
    
    // splitPosition 값을 사용하여 분할 비율 계산 (기본값 50)
    const split = splitPosition || 50;
    const leftRatio = split / 100;
    const rightRatio = (100 - split) / 100;
    const gap = 0.02; // 2% 여백
    
    switch (slideTheme) {
      case 'left-split':
        // 왼쪽 분할: 모든 요소를 왼쪽 영역에 배치
        newElement.width = element.width * (leftRatio - gap);
        newElement.x = element.x * (leftRatio - gap);
        break;
      
      case 'right-split':
        // 오른쪽 분할: 모든 요소를 오른쪽 영역에 배치
        newElement.width = element.width * (rightRatio - gap);
        newElement.x = aspectRatio.width * (leftRatio + gap) + (element.x * (rightRatio - gap));
        break;
      
      case 'top-split':
        // 상단 분할: 모든 요소를 상단 영역에 배치
        newElement.height = element.height * (leftRatio - gap);
        newElement.y = element.y * (leftRatio - gap);
        break;
      
      case 'bottom-split':
        // 하단 분할: 모든 요소를 하단 영역에 배치
        newElement.height = element.height * (rightRatio - gap);
        newElement.y = aspectRatio.height * (leftRatio + gap) + (element.y * (rightRatio - gap));
        break;
      
      case 'image-card':
        // 이미지 카드 스타일: 요소를 카드 형태로 배치
        // 약간의 패딩과 그림자 효과 추가
        newElement.width = element.width * 0.9;
        newElement.height = element.height * 0.9;
        newElement.x = element.x + (element.width * 0.05);
        newElement.y = element.y + (element.height * 0.05);
        // 배경색과 그림자 추가
        if (!newElement.backgroundColor || newElement.backgroundColor === 'transparent') {
          newElement.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        }
        newElement.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
        newElement.borderRadius = 12;
        break;
      
      case 'diagonal': {
        // 대각선 분할: 요소를 대각선으로 배치
        // 왼쪽 상단과 오른쪽 하단으로 분할
        const isUpperLeft = (element.x + element.width/2) < (aspectRatio.width/2) && 
                           (element.y + element.height/2) < (aspectRatio.height/2);
        const isLowerRight = (element.x + element.width/2) >= (aspectRatio.width/2) && 
                            (element.y + element.height/2) >= (aspectRatio.height/2);
        
        if (isUpperLeft || isLowerRight) {
          // 그대로 유지
        } else {
          // 위치 조정
          newElement.x = aspectRatio.width - element.x - element.width;
          newElement.y = aspectRatio.height - element.y - element.height;
        }
        break;
      }
    }
    
    return newElement;
  };

  // 컨테이너 변형 효과 생성 함수
  const getContainerTransform = () => {
    if (backgroundPattern && backgroundPattern !== 'none') {
      const patternStyle = getPatternStyle(backgroundPattern);
      if (patternStyle.includes('transform:') || patternStyle.includes('filter:')) {
        return patternStyle;
      }
    }
    return '';
  };

  // 분할선 경로 생성 함수
  // 분할 영역의 클립 패스 생성 함수
  const getDividerClipPath = (type: string, position: 'left' | 'right' | 'top' | 'bottom', splitPercent = 50) => {
    const id = `clip-${type}-${position}-${Date.now()}`;
    
    switch (type) {
      case 'straight':
        if (position === 'left') {
          return {
            id,
            path: `<clipPath id="${id}"><polygon points="0,0 ${splitPercent},0 ${splitPercent},100 0,100" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><polygon points="${splitPercent},0 100,0 100,100 ${splitPercent},100" /></clipPath>`
          };
        } else if (position === 'right') {
          return {
            id,
            path: `<clipPath id="${id}"><polygon points="${100-splitPercent},0 100,0 100,100 ${100-splitPercent},100" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><polygon points="0,0 ${100-splitPercent},0 ${100-splitPercent},100 0,100" /></clipPath>`
          };
        } else if (position === 'top') {
          return {
            id,
            path: `<clipPath id="${id}"><polygon points="0,0 100,0 100,${splitPercent} 0,${splitPercent}" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><polygon points="0,${splitPercent} 100,${splitPercent} 100,100 0,100" /></clipPath>`
          };
        } else { // bottom
          return {
            id,
            path: `<clipPath id="${id}"><polygon points="0,${100-splitPercent} 100,${100-splitPercent} 100,100 0,100" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><polygon points="0,0 100,0 100,${100-splitPercent} 0,${100-splitPercent}" /></clipPath>`
          };
        }
      
      case 'convex':
        if (position === 'left') {
          return {
            id,
            path: `<clipPath id="${id}"><path d="M0,0 L${splitPercent},0 Q${splitPercent-20},50 ${splitPercent},100 L0,100 Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M${splitPercent},0 Q${splitPercent-20},50 ${splitPercent},100 L100,100 L100,0 Z" /></clipPath>`
          };
        } else if (position === 'right') {
          return {
            id,
            path: `<clipPath id="${id}"><path d="M${100-splitPercent},0 L100,0 L100,100 L${100-splitPercent},100 Q${100-splitPercent+20},50 ${100-splitPercent},0 Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M0,0 L${100-splitPercent},0 Q${100-splitPercent+20},50 ${100-splitPercent},100 L0,100 Z" /></clipPath>`
          };
        } else if (position === 'top') {
          return {
            id,
            path: `<clipPath id="${id}"><path d="M0,0 L100,0 L100,${splitPercent} Q50,${splitPercent-20} 0,${splitPercent} Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M0,${splitPercent} Q50,${splitPercent-20} 100,${splitPercent} L100,100 L0,100 Z" /></clipPath>`
          };
        } else { // bottom
          return {
            id,
            path: `<clipPath id="${id}"><path d="M0,${100-splitPercent} Q50,${100-splitPercent+20} 100,${100-splitPercent} L100,100 L0,100 Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M0,0 L100,0 L100,${100-splitPercent} Q50,${100-splitPercent+20} 0,${100-splitPercent} Z" /></clipPath>`
          };
        }
      
      case 'concave':
        if (position === 'left') {
          return {
            id,
            path: `<clipPath id="${id}"><path d="M0,0 L${splitPercent},0 Q${splitPercent+20},50 ${splitPercent},100 L0,100 Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M${splitPercent},0 Q${splitPercent+20},50 ${splitPercent},100 L100,100 L100,0 Z" /></clipPath>`
          };
        } else if (position === 'right') {
          return {
            id,
            path: `<clipPath id="${id}"><path d="M${100-splitPercent},0 L100,0 L100,100 L${100-splitPercent},100 Q${100-splitPercent-20},50 ${100-splitPercent},0 Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M0,0 L${100-splitPercent},0 Q${100-splitPercent-20},50 ${100-splitPercent},100 L0,100 Z" /></clipPath>`
          };
        } else if (position === 'top') {
          return {
            id,
            path: `<clipPath id="${id}"><path d="M0,0 L100,0 L100,${splitPercent} Q50,${splitPercent+20} 0,${splitPercent} Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M0,${splitPercent} Q50,${splitPercent+20} 100,${splitPercent} L100,100 L0,100 Z" /></clipPath>`
          };
        } else { // bottom
          return {
            id,
            path: `<clipPath id="${id}"><path d="M0,${100-splitPercent} Q50,${100-splitPercent-20} 100,${100-splitPercent} L100,100 L0,100 Z" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M0,0 L100,0 L100,${100-splitPercent} Q50,${100-splitPercent-20} 0,${100-splitPercent} Z" /></clipPath>`
          };
        }
      
      case 'zigzag':
        if (position === 'left') {
          const zigzagPath = `M0,0 L${splitPercent},0 L${splitPercent-5},10 L${splitPercent},20 L${splitPercent-5},30 L${splitPercent},40 L${splitPercent-5},50 L${splitPercent},60 L${splitPercent-5},70 L${splitPercent},80 L${splitPercent-5},90 L${splitPercent},100 L0,100 Z`;
          return {
            id,
            path: `<clipPath id="${id}"><path d="${zigzagPath}" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M${splitPercent},0 L${splitPercent-5},10 L${splitPercent},20 L${splitPercent-5},30 L${splitPercent},40 L${splitPercent-5},50 L${splitPercent},60 L${splitPercent-5},70 L${splitPercent},80 L${splitPercent-5},90 L${splitPercent},100 L100,100 L100,0 Z" /></clipPath>`
          };
        }
        // 다른 방향들도 비슷하게 구현
        break;
      
      case 'cloud':
        if (position === 'left') {
          const cloudPath = `M0,0 L${splitPercent},0 Q${splitPercent-10},10 ${splitPercent},20 Q${splitPercent-8},30 ${splitPercent},40 Q${splitPercent-12},50 ${splitPercent},60 Q${splitPercent-6},70 ${splitPercent},80 Q${splitPercent-10},90 ${splitPercent},100 L0,100 Z`;
          return {
            id,
            path: `<clipPath id="${id}"><path d="${cloudPath}" /></clipPath>`,
            contentClip: `<clipPath id="${id}-content"><path d="M${splitPercent},0 Q${splitPercent-10},10 ${splitPercent},20 Q${splitPercent-8},30 ${splitPercent},40 Q${splitPercent-12},50 ${splitPercent},60 Q${splitPercent-6},70 ${splitPercent},80 Q${splitPercent-10},90 ${splitPercent},100 L100,100 L100,0 Z" /></clipPath>`
          };
        }
        break;
        
      default:
        return getDividerClipPath('straight', position, splitPercent);
    }
    
    // 기본값 반환
    return getDividerClipPath('straight', position, splitPercent);
  };

  const getDividerPath = (type: string, position: 'left' | 'right' | 'top' | 'bottom') => {
    switch (type) {
      case 'straight':
        if (position === 'left' || position === 'right') {
          return '<path d="M 100 0 L 100 100" fill="none" />';
        } else {
          return '<path d="M 0 100 L 100 100" fill="none" />';
        }
      
      case 'zigzag':
        if (position === 'left' || position === 'right') {
          return '<path d="M 100 0 L 105 10 L 95 20 L 105 30 L 95 40 L 105 50 L 95 60 L 105 70 L 95 80 L 105 90 L 100 100" fill="none" />';
        } else {
          return '<path d="M 0 100 L 5 85 L 10 100 L 15 88 L 20 100 L 25 86 L 30 100 L 35 87 L 40 100 L 45 85 L 50 100 L 55 88 L 60 100 L 65 86 L 70 100 L 75 87 L 80 100 L 85 85 L 90 100 L 95 88 L 100 100" fill="none" />';
        }
      
      case 'gradient':
        // Gradient doesn't need a path, handled differently
        return '';
      
      case 'random':
        // Random curve
        if (position === 'left' || position === 'right') {
          const points = Array.from({ length: 5 }, (_, i) => {
            const y = (i + 1) * 20;
            const x = 100 + (Math.random() - 0.5) * 20;
            return `${x} ${y}`;
          });
          return `<path d="M 100 0 ${points.map((p, i) => (i === 0 ? 'L' : '') + ' ' + p).join(' ')} L 100 100" fill="none" />`;
        } else {
          const points = Array.from({ length: 5 }, (_, i) => {
            const x = (i + 1) * 20;
            const y = 100 + (Math.random() - 0.5) * 20;
            return `${x} ${y}`;
          });
          return `<path d="M 0 100 ${points.map((p, i) => (i === 0 ? 'L' : '') + ' ' + p).join(' ')} L 100 100" fill="none" />`;
        }
      
      default:
        return type === 'straight' ? 
          (position === 'left' || position === 'right' ? '<path d="M 100 0 L 100 100" fill="none" />' : '<path d="M 0 100 L 100 100" fill="none" />') : 
          '';
    }
  };

  // slideTheme에 따른 스플릿 레이아웃 생성 함수
  const getSplitLayout = () => {
    if (slideTheme === 'default') {
      return '';
    }

    // image-card는 하단에 이미지 배경을 표시
    if (slideTheme === 'image-card') {
      return `
        <div style="
          position: absolute;
          left: 0;
          bottom: 0;
          right: 0;
          height: 30%;
          background: url('${backgroundImage}') center/cover;
          z-index: 1;
        "></div>
      `;
    }

    // diagonal은 대각선 배경 효과
    if (slideTheme === 'diagonal') {
      return `
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, 
            transparent 48%, 
            ${theme.primary}20 48%, 
            ${theme.primary}20 52%, 
            transparent 52%);
          z-index: 1;
          pointer-events: none;
        "></div>
        ${backgroundImage ? `
          <div style="
            position: absolute;
            top: 0;
            left: 0;
            width: 50%;
            height: 50%;
            background: url('${backgroundImage}') center/cover;
            clip-path: polygon(0 0, 100% 0, 0 100%);
            z-index: 1;
          "></div>
          <div style="
            position: absolute;
            bottom: 0;
            right: 0;
            width: 50%;
            height: 50%;
            background: url('${backgroundImage}') center/cover;
            clip-path: polygon(100% 0, 100% 100%, 0 100%);
            z-index: 1;
          "></div>
        ` : ''}
      `;
    }

    const splitDirection = slideTheme.replace('-split', '');
    const isHorizontal = splitDirection === 'left' || splitDirection === 'right';
    const isVertical = splitDirection === 'top' || splitDirection === 'bottom';
    
    // splitPosition 값을 사용하여 분할 비율 계산 (기본값 50)
    const split = splitPosition || 50;
    const leftRatio = split;
    const rightRatio = 100 - split;

    // 분할 영역 스타일
    const splitStyles = {
      left: {
        backgroundArea: `left: 0; top: 0; width: ${leftRatio}%; height: 100%;`,
        contentArea: `right: 0; top: 0; width: ${rightRatio}%; height: 100%;`,
        divider: `left: ${leftRatio}%; top: 0; width: 2px; height: 100%;`
      },
      right: {
        backgroundArea: `right: 0; top: 0; width: ${rightRatio}%; height: 100%;`,
        contentArea: `left: 0; top: 0; width: ${leftRatio}%; height: 100%;`,
        divider: `left: ${leftRatio}%; top: 0; width: 2px; height: 100%;`
      },
      top: {
        backgroundArea: `left: 0; top: 0; width: 100%; height: ${leftRatio}%;`,
        contentArea: `left: 0; bottom: 0; width: 100%; height: ${rightRatio}%;`,
        divider: `left: 0; top: ${leftRatio}%; width: 100%; height: 2px;`
      },
      bottom: {
        backgroundArea: `left: 0; bottom: 0; width: 100%; height: ${rightRatio}%;`,
        contentArea: `left: 0; top: 0; width: 100%; height: ${leftRatio}%;`,
        divider: `left: 0; top: ${leftRatio}%; width: 100%; height: 2px;`
      }
    };

    const style = splitStyles[splitDirection as keyof typeof splitStyles];
    if (!style) {return '';}

    // 배경 이미지 영역 생성
    const backgroundAreaHtml = backgroundImage ? `
      <div style="
        position: absolute;
        ${style.backgroundArea}
        background: url('${backgroundImage}') center/cover;
        z-index: 1;
      "></div>
    ` : '';

    // gradient divider의 경우 특별 처리
    if (dividerType === 'gradient') {
      const gradientDirection = isHorizontal ? 'to right' : 'to bottom';
      return `
        ${backgroundAreaHtml}
        <div style="position: absolute; ${style.divider} 
          background: linear-gradient(${gradientDirection}, 
            transparent 0%, 
            ${theme.primary}40 20%, 
            ${theme.primary}60 50%, 
            ${theme.primary}40 80%, 
            transparent 100%);
          z-index: 10;"></div>
      `;
    }

    // SVG 기반 divider
    const dividerPath = getDividerPath(dividerType, splitPosition as any);
    if (!dividerPath) {return '';}

    return `
      ${backgroundAreaHtml}
      <svg style="position: absolute; ${style.divider} z-index: 10;" 
           viewBox="0 0 100 100" 
           preserveAspectRatio="none">
        <defs>
          <filter id="divider-shadow">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="0" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <g filter="url(#divider-shadow)">
          ${dividerPath.replace('fill="none"', `stroke="${theme.primary}60" stroke-width="2" fill="none"`)}
        </g>
      </svg>
    `;
  };
  
  // 젠스파크 스타일 프리미엄 HTML 생성 조건을 제거하고 일단 기본 HTML로 처리
  // TODO: 프리미엄 HTML은 별도 처리 필요

  return `
    <div class="slide-container" style="
      width: 100%;
      aspect-ratio: ${aspectRatio.width}/${aspectRatio.height};
      border: ${boxStyles.border};
      border-radius: ${boxStyles.borderRadius};
      background-color: ${boxStyles.backgroundColor};
      padding: ${boxStyles.padding};
      display: flex;
      flex-direction: column;
      ${layoutStyles.container};
      position: relative;
      overflow: hidden;
      font-family: ${fontStyles.fontFamily};
      text-shadow: ${fontStyles.textShadow};
      -webkit-text-stroke: ${fontStyles.webkitTextStroke};
      paint-order: ${fontStyles.paintOrder};
      box-shadow: ${boxStyles.boxShadow};
      min-height: 600px;
      max-width: 90vw;
      max-height: 80vh;
      ${getContainerTransform()}
    ">
      <div style="
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        ${getBackgroundStyle()}
        filter: blur(${backgroundBlur}px);
        z-index: 0;
      "></div>
      
      ${backgroundPattern && backgroundPattern !== 'none' ? `
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          ${getPatternStyle(backgroundPattern)}
          z-index: 1;
          pointer-events: none;
        "></div>
      ` : ''}
      
      ${allElements && allElements.length > 0 ? allElements.map(element => {
        const adjustedElement = adjustElementPosition(element);
        return `
        <div style="
          position: absolute;
          left: ${adjustedElement.x}px;
          top: ${adjustedElement.y}px;
          width: ${adjustedElement.width}px;
          height: ${adjustedElement.height}px;
          transform: rotate(${adjustedElement.rotation || 0}deg);
          z-index: ${adjustedElement.zIndex || 2}; /* Adjusted for pattern overlay */
          background-color: ${adjustedElement.backgroundColor || 'transparent'};
          border: ${adjustedElement.borderWidth || 0}px solid ${adjustedElement.borderColor || 'transparent'};
        ">
          ${(() => {
            switch (element.type) {
              case 'text':
                return `
                  <div style="
                    width: 100%;
                    height: 100%;
                    font-size: ${element.fontSize}px;
                    font-family: ${element.fontFamily};
                    color: ${element.color};
                    font-weight: ${element.fontWeight};
                    text-align: ${element.textAlign};
                    line-height: 1.4;
                    padding: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: ${element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start'};
                    word-break: break-word;
                    white-space: pre-wrap;
                    text-shadow: ${backgroundImage ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none'};
                  ">
                    ${wrapLists(renderMarkdown(element.content || ''))}
                  </div>
                `;
              
              case 'div':
                return `
                  <div style="
                    width: 100%;
                    height: 100%;
                    padding: ${element.padding || 16}px;
                    margin: ${element.margin || 0}px;
                    border-radius: ${element.borderRadius || 8}px;
                    border-left: ${element.leftBorderWidth || 4}px solid ${element.leftBorderColor || '#00356B'};
                    background: ${element.gradient || element.backgroundColor || 'rgba(235, 244, 255, 0.8)'};
                    box-shadow: ${element.boxShadow || '0 2px 8px rgba(0,0,0,0.1)'};
                    font-size: ${element.fontSize}px;
                    font-family: ${element.fontFamily};
                    color: ${element.color};
                    line-height: 1.5;
                  ">
                    ${wrapLists(renderMarkdown(element.content || ''))}
                  </div>
                `;
              
              case 'list': {
                const listItems = element.listItems || element.content.split('\n').filter(item => item.trim());
                const listTypeIcon = element.listType === 'number' ? '' : element.listType === 'emoji' ? '💡' : '•';
                return `
                  <div style="
                    width: 100%;
                    height: 100%;
                    padding: ${element.padding || 12}px;
                    font-size: ${element.fontSize}px;
                    font-family: ${element.fontFamily};
                    color: ${element.color};
                  ">
                    ${listItems.map((item, index) => `
                      <div style="
                        display: flex;
                        align-items: flex-start;
                        margin-bottom: 8px;
                        padding: 6px 12px;
                        background: rgba(235, 244, 255, 0.6);
                        border-radius: 6px;
                        border-left: 3px solid ${element.leftBorderColor || '#00356B'};
                      ">
                        <span style="margin-right: 10px; font-weight: bold; color: ${element.leftBorderColor || '#00356B'};">
                          ${element.listType === 'number' ? `${index + 1}.` : listTypeIcon}
                        </span>
                        <span>${item.trim()}</span>
                      </div>
                    `).join('')}
                  </div>
                `;
              }
              
              case 'column': {
                const columnContent = element.content.split('|||').map(col => col.trim());
                return `
                  <div style="
                    width: 100%;
                    height: 100%;
                    display: grid;
                    grid-template-columns: repeat(${element.columnCount || 2}, 1fr);
                    gap: ${element.columnGap || 20}px;
                    padding: ${element.padding || 16}px;
                    font-size: ${element.fontSize}px;
                    font-family: ${element.fontFamily};
                    color: ${element.color};
                  ">
                    ${columnContent.map(colContent => `
                      <div style="
                        padding: 12px;
                        background: ${element.backgroundColor || 'rgba(255, 255, 255, 0.8)'};
                        border-radius: ${element.borderRadius || 8}px;
                        border-left: 4px solid ${element.leftBorderColor || '#00356B'};
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                      ">
                        ${wrapLists(renderMarkdown(colContent))}
                      </div>
                    `).join('')}
                  </div>
                `;
              }
              
              case 'chart':
                if (element.chartData && element.chartData.data && element.chartData.data.labels && element.chartData.data.values) {
                  const chartId = `chart-${Date.now()}-${Math.random()}`;
                  const chartData = element.chartData;
                  
                  // 차트 플레이스홀더 - 실제 렌더링은 EChartsRenderer에서 처리
                  return `
                    <div style="
                      width: 100%;
                      height: 100%;
                      padding: ${element.padding || 16}px;
                      background: ${element.backgroundColor || 'rgba(255, 255, 255, 0.95)'};
                      border-radius: ${element.borderRadius || 8}px;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      justify-content: center;
                    ">
                      ${chartData.title ? `<h4 style="margin: 0 0 12px 0; font-size: ${element.fontSize * 1.2}px; color: ${element.color};">${chartData.title}</h4>` : ''}
                      <div id="chart-${element.id}" style="
                        flex: 1;
                        width: 100%;
                        height: 100%;
                        min-height: 300px;
                      "></div>
                      <script src="https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js"></script>
                      <script>
                        (function() {
                          var chartDom = document.getElementById('chart-${element.id}');
                          var myChart = echarts.init(chartDom);
                          var option = ${JSON.stringify(getChartOption(chartData, theme))};
                          myChart.setOption(option);
                          window.addEventListener('resize', function() {
                            myChart.resize();
                          });
                        })();
                      </script>
                    </div>
                  `;
                }
                return '';
              
              case 'image':
                return element.content ? `
                  <img 
                    src="${element.content}" 
                    alt="Slide element" 
                    style="
                      width: 100%;
                      height: 100%;
                      object-fit: cover;
                      border-radius: ${element.borderRadius || 4}px;
                    "
                  />
                ` : '';
              
              case 'container': {
                if (!element.containerItems || !element.containerLayout) {
                  return '';
                }
                
                const items = element.containerItems;
                const layout = element.containerLayout;
                const containerWidth = parseFloat(adjustedElement.width);
                
                // 컨테이너 비율 확인 (긴 바 타입: 가로가 세로보다 2배 이상 긴 경우)
                const containerHeight = parseFloat(adjustedElement.height);
                const isHorizontalBarType = containerWidth > containerHeight * 2;
                
                // 동적으로 컬럼 수 결정
                let columns = element.containerColumns || 'auto';
                if (columns === 'auto') {
                  // 3개 이상이면 긴 바 타입이 아닌 경우 1열로
                  if (items.length >= 3 && !isHorizontalBarType) {
                    columns = 1;
                  } else {
                    columns = containerWidth < 300 ? 1 : containerWidth < 500 ? 2 : 3;
                  }
                }
                
                // 사용 가능한 공간 계산 (패딩 제외)
                const contentWidth = containerWidth - 32; // 패딩 16px * 2
                const contentHeight = containerHeight - 32;
                const itemCount = items.length;
                
                // 겹침 방지를 위한 엄격한 크기 계산
                let availableHeightPerItem, availableWidthPerItem;
                
                if (layout === 'header-list' || columns === 1) {
                  // 세로 배치
                  availableHeightPerItem = contentHeight / itemCount;
                  availableWidthPerItem = contentWidth;
                } else {
                  // 그리드 배치
                  const rows = Math.ceil(itemCount / columns);
                  availableHeightPerItem = contentHeight / rows;
                  availableWidthPerItem = contentWidth / columns;
                }
                
                // 아이콘 크기: 사용 가능한 공간의 30% 이하로 제한
                const maxIconSizeFromHeight = availableHeightPerItem * 0.3;
                const maxIconSizeFromWidth = availableWidthPerItem * 0.15;
                const maxIconSize = Math.min(maxIconSizeFromHeight, maxIconSizeFromWidth);
                const iconSize = Math.max(16, Math.min(48, maxIconSize));
                
                // 레이아웃별 패딩과 간격 최적화
                let basePadding, baseGap;
                
                if (layout === 'header-list' || columns === 1) {
                  // 1열 배치: 간격 최소화, 패딩 줄이기
                  basePadding = Math.max(6, Math.min(10, availableHeightPerItem * 0.04));
                  baseGap = Math.max(3, basePadding * 0.3);
                } else if (itemCount <= columns) {
                  // 1행 배치: 좌우 간격 늘리기, 위아래 패딩 줄이기
                  basePadding = Math.max(8, Math.min(16, availableWidthPerItem * 0.03));
                  baseGap = Math.max(8, Math.min(24, availableWidthPerItem * 0.05));
                } else {
                  // 다중 행/열: 기본값
                  basePadding = Math.max(4, Math.min(12, Math.min(availableWidthPerItem, availableHeightPerItem) * 0.08));
                  baseGap = Math.max(4, basePadding * 0.5);
                }
                
                // 테마 색상 적용
                const textColor = element.color || theme.secondary || '#1E40AF';
                const themeBackground = theme.accent || theme.quaternary || '#F59E0B';
                
                // 레이아웃별 스타일 정의
                const getLayoutStyles = () => {
                  switch (layout) {
                    case 'icon-grid':
                      return {
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gridTemplateRows: `repeat(${Math.ceil(itemCount / columns)}, 1fr)`,
                        gap: `${baseGap}px`,
                        alignItems: 'center',
                        alignContent: 'center',
                        justifyItems: 'center',
                        width: '100%',
                        height: '100%'
                      };
                    case 'header-list':
                      return {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${baseGap}px`,
                        justifyContent: itemCount <= 3 ? 'space-evenly' : 'flex-start',
                        alignItems: 'stretch',
                        width: '100%',
                        height: '100%'
                      };
                    case 'card-grid':
                      return {
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gridTemplateRows: `repeat(${Math.ceil(itemCount / columns)}, 1fr)`,
                        gap: `${baseGap}px`,
                        alignItems: 'center',
                        alignContent: 'center',
                        justifyItems: 'center',
                        width: '100%',
                        height: '100%'
                      };
                    case 'column-layout':
                      return {
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.min(itemCount, 3)}, 1fr)`,
                        gap: `${baseGap * 1.5}px`,
                        alignItems: 'stretch',
                        alignContent: 'center',
                        justifyItems: 'stretch',
                        width: '100%',
                        height: '100%'
                      };
                    default:
                      return { padding: `${basePadding}px`, height: '100%' };
                  }
                };
                
                const containerStyles = getLayoutStyles();
                
                const containerPadding = layout === 'header-list' || columns === 1 ? 
                                         Math.max(8, Math.min(12, containerHeight * 0.02)) : 
                                         itemCount <= columns ? 
                                         Math.max(6, Math.min(8, containerHeight * 0.015)) : 
                                         16;
                
                return `
                  <div style="
                    width: 100%;
                    height: 100%;
                    padding: ${containerPadding}px;
                    background: ${element.backgroundColor || (theme.accent ? theme.accent + '20' : theme.quaternary ? theme.quaternary + '20' : '#F59E0B20')};
                    border-radius: ${element.borderRadius || 8}px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                  ">
                    <div style="flex: 1; ${Object.entries(containerStyles).map(([key, value]) => 
                      `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
                    ).join(' ')}">
                    ${items.map((item, index) => {
                      
                      switch (layout) {
                        case 'icon-grid':
                          return `
                            <div style="
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                              text-align: center;
                              padding: ${basePadding}px;
                              background: ${themeBackground}15;
                              border-radius: 8px;
                              transition: transform 0.2s;
                              min-height: fit-content;
                              max-height: ${availableHeightPerItem - baseGap}px;
                              width: 100%;
                              box-sizing: border-box;
                              overflow: hidden;
                            ">
                              <div style="font-size: ${iconSize}px; line-height: 1; margin-bottom: ${basePadding * 0.3}px; flex-shrink: 0;">${item.icon}</div>
                              ${item.title ? `<div style="font-weight: bold; margin-bottom: ${basePadding * 0.2}px; font-size: ${Math.min(element.fontSize, availableHeightPerItem * 0.15)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.1; flex-shrink: 0;">${item.title}</div>` : ''}
                              <div style="font-size: ${Math.min(element.fontSize * 0.85, availableHeightPerItem * 0.12)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;">${item.content}</div>
                            </div>
                          `;
                        case 'header-list':
                          return `
                            <div style="
                              display: flex;
                              align-items: center;
                              gap: ${basePadding}px;
                              padding: ${basePadding}px;
                              background: ${themeBackground}10;
                              border-radius: 6px;
                              min-height: fit-content;
                              max-height: ${availableHeightPerItem - baseGap}px;
                              width: 100%;
                              box-sizing: border-box;
                              overflow: hidden;
                            ">
                              <div style="font-size: ${iconSize * 0.8}px; flex-shrink: 0; line-height: 1;">${item.icon}</div>
                              <div style="font-size: ${Math.min(element.fontSize, availableHeightPerItem * 0.25)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; flex: 1;">${item.content}</div>
                            </div>
                          `;
                        case 'card-grid':
                          return `
                            <div style="
                              padding: ${basePadding}px;
                              background: ${themeBackground}15;
                              border-radius: 12px;
                              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                              min-height: fit-content;
                              max-height: ${availableHeightPerItem - baseGap}px;
                              width: 100%;
                              box-sizing: border-box;
                              overflow: hidden;
                              display: flex;
                              flex-direction: column;
                            ">
                              <div style="font-size: ${iconSize}px; line-height: 1; margin-bottom: ${basePadding * 0.3}px; flex-shrink: 0;">${item.icon}</div>
                              ${item.title ? `<div style="font-weight: bold; margin-bottom: ${basePadding * 0.2}px; font-size: ${Math.min(element.fontSize, availableHeightPerItem * 0.15)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.1; flex-shrink: 0;">${item.title}</div>` : ''}
                              <div style="font-size: ${Math.min(element.fontSize * 0.85, availableHeightPerItem * 0.12)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; flex: 1;">${item.content}</div>
                            </div>
                          `;
                        case 'column-layout':
                          return `
                            <div style="
                              padding: 16px;
                              background: ${themeBackground}15;
                              border-radius: 8px;
                              height: 100%;
                              box-sizing: border-box;
                              overflow: hidden;
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                            ">
                              <div style="font-size: ${iconSize * 1.2}px; line-height: 1; margin-bottom: ${basePadding}px; text-align: center;">${item.icon}</div>
                              ${item.title ? `<h3 style="font-weight: bold; margin-bottom: ${basePadding * 0.8}px; font-size: ${Math.min(element.fontSize * 1.1, availableHeightPerItem * 0.2)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.3; text-align: center;">${item.title}</h3>` : ''}
                              <div style="font-size: ${Math.min(element.fontSize * 0.9, availableHeightPerItem * 0.13)}px; font-family: ${fontStyles.fontFamily || element.fontFamily || 'Arial, sans-serif'}; color: ${textColor}; line-height: 1.4; text-align: center; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; flex: 1;">${item.content}</div>
                            </div>
                          `;
                        default:
                          return `<div>${item.icon} ${item.content}</div>`;
                      }
                    }).join('')}
                    </div>
                  </div>
                `;
              }
              
              default:
                return '';
            }
          })()}
        </div>
      `;
      }).join('') : ''}
      
      ${getSplitLayout()}
    </div>
  `;
};

// 젠스파크 스타일 프리미엄 슬라이드 HTML 생성 함수
export const generatePremiumSlideHTML = (
  title: string,
  content: string,
  theme: Theme,
  aspectRatio: AspectRatio,
  slideIndex: number,
  layout: string
): string => {
  // 슬라이드 유형 결정 (인덱스 기반)
  const getSlideTemplate = (index: number) => {
    if (index === 0) {return 'titlePage';}
    // 6개 이상일 때는 더 다양한 패턴
    const patterns = ['conceptIntro', 'categoryGrid', 'detailAnalysis', 'conclusionSummary'];
    return patterns[index % patterns.length] || 'conceptIntro';
  };

  const template = getSlideTemplate(slideIndex);
  
  // 프로페셔널 색상 팔레트
  const colors = {
    primary: '#00356B',
    secondary: '#2c5282',
    accent: '#A51C30',
    background: '#f8fafc',
    text: '#1a365d',
    lightBg: 'rgba(255, 255, 255, 0.8)',
    cardBg: 'rgba(235, 244, 255, 0.8)'
  };

  // 공통 스타일
  const commonStyles = `
    <style>
      .slide-container {
        font-family: 'Noto Sans KR', sans-serif;
        overflow: hidden;
      }
      .title {
        font-size: 2.5rem;
        font-weight: 700;
        color: ${colors.primary};
        line-height: 1.2;
      }
      .subtitle {
        font-size: 1.4rem;
        font-weight: 500;
        color: ${colors.secondary};
        margin-top: 0.5rem;
      }
      .content-text {
        font-size: 1.2rem;
        line-height: 1.6;
        color: ${colors.text};
      }
      .content-box {
        background-color: ${colors.lightBg};
        border-left: 4px solid ${colors.primary};
        padding: 16px;
        margin: 12px 0;
        border-radius: 0 4px 4px 0;
      }
      .content-box-title {
        font-weight: 600;
        color: ${colors.primary};
        font-size: 1.25rem;
        margin-bottom: 8px;
      }
      .accent-line {
        height: 4px;
        width: 100px;
        background-color: ${colors.accent};
        margin: 12px 0 24px 0;
      }
      .card-component {
        background-color: ${colors.cardBg};
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 12px;
      }
      .icon {
        color: ${colors.primary};
        margin-right: 10px;
      }
      .feature-dot {
        width: 12px;
        height: 12px;
        background-color: ${colors.accent};
        border-radius: 50%;
        display: inline-block;
        margin-right: 12px;
      }
    </style>
  `;

  // 배경 아이콘 SVG
  const backgroundIcon = `
    <svg style="
      position: absolute;
      width: 400px;
      height: 400px;
      opacity: 0.04;
      top: 50%;
      right: 5%;
      transform: translateY(-50%);
      z-index: 0;
    " xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
      <path fill="currentColor" d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm80 248c0 44.112-35.888 80-80 80s-80-35.888-80-80 35.888-80 80-80 80 35.888 80 80z"/>
    </svg>
  `;

  // 템플릿별 HTML 생성
  const generateTemplateHTML = () => {
    const contentLines = content.split('\n').filter(line => line.trim());
    
    switch (template) {
      case 'titlePage':
        return `
          <div style="text-align: center; padding: 80px 40px;">
            <h1 class="title">${title}</h1>
            <div class="accent-line" style="margin: 24px auto;"></div>
            <p class="content-text" style="max-width: 800px; margin: 0 auto;">
              ${content}
            </p>
          </div>
        `;
        
      case 'categoryGrid': {
        // 내용을 카드로 분할
        const cards = contentLines.slice(0, 4).map((line, i) => `
          <div class="card-component" style="width: 48%; margin: 1%;">
            <div style="text-align: center;">
              <i class="fas fa-${['lightbulb', 'cogs', 'chart-line', 'users'][i]} icon" style="font-size: 2rem; margin-bottom: 12px;"></i>
              <h3 style="color: ${colors.primary}; margin-bottom: 8px;">${line.substring(0, 20)}...</h3>
              <p style="font-size: 0.9rem; color: ${colors.text};">${line.length > 20 ? line.substring(20) : line}</p>
            </div>
          </div>
        `).join('');
        
        return `
          <div style="padding: 40px;">
            <h1 class="title">${title}</h1>
            <div class="accent-line"></div>
            <div style="display: flex; flex-wrap: wrap; justify-content: space-between;">
              ${cards}
            </div>
          </div>
        `;
      }
        
      case 'detailAnalysis': {
        const leftContent = contentLines.slice(0, Math.ceil(contentLines.length/2));
        const rightContent = contentLines.slice(Math.ceil(contentLines.length/2));
        
        return `
          <div style="padding: 40px;">
            <h1 class="title">${title}</h1>
            <div class="accent-line"></div>
            <div style="display: flex; gap: 40px;">
              <div style="flex: 1;">
                <div class="content-box">
                  <div class="content-box-title">주요 내용</div>
                  ${leftContent.map(line => `<p style="margin-bottom: 8px;"><span class="feature-dot"></span>${line}</p>`).join('')}
                </div>
              </div>
              <div style="flex: 1;">
                <div class="content-box">
                  <div class="content-box-title">세부 분석</div>
                  ${rightContent.map(line => `<p style="margin-bottom: 8px;"><span class="feature-dot"></span>${line}</p>`).join('')}
                </div>
              </div>
            </div>
          </div>
        `;
      }
        
      case 'conclusionSummary':
        return `
          <div style="padding: 40px;">
            <h1 class="title">${title}</h1>
            <div class="accent-line"></div>
            <div class="content-box">
              <div class="content-box-title">핵심 요약</div>
              ${contentLines.map((line, i) => `
                <div style="display: flex; align-items: center; margin-bottom: 12px;">
                  <div style="
                    width: 28px; height: 28px; 
                    background-color: ${colors.primary}; 
                    color: white; 
                    border-radius: 50%; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center;
                    font-weight: 600;
                    margin-right: 12px;
                  ">${i + 1}</div>
                  <span>${line}</span>
                </div>
              `).join('')}
            </div>
          </div>
        `;
        
      default: // conceptIntro
        return `
          <div style="padding: 40px;">
            <h1 class="title">${title}</h1>
            <div class="accent-line"></div>
            <div style="display: flex; gap: 40px;">
              <div style="flex: 2;">
                <div class="content-box">
                  <div class="content-box-title">개념 정의</div>
                  <p class="content-text">${content}</p>
                </div>
              </div>
              <div style="flex: 1;">
                <div class="content-box">
                  <div class="content-box-title">핵심 포인트</div>
                  ${contentLines.slice(0, 3).map(line => `<p style="margin-bottom: 8px;"><span class="feature-dot"></span>${line.substring(0, 50)}...</p>`).join('')}
                </div>
              </div>
            </div>
          </div>
        `;
    }
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700&display=swap">
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.4.0/css/all.min.css">
      ${commonStyles}
    </head>
    <body>
      <div class="slide-container" style="
        width: 100%;
        aspect-ratio: ${aspectRatio.width}/${aspectRatio.height};
        min-height: 720px;
        position: relative;
        background-color: ${colors.background};
        color: ${colors.text};
        overflow: hidden;
      ">
        ${backgroundIcon}
        <div style="position: relative; z-index: 1; height: 100%;">
          ${generateTemplateHTML()}
        </div>
        <div style="
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 6px;
          background: linear-gradient(to right, ${colors.primary}, ${colors.secondary});
        "></div>
      </div>
    </body>
    </html>
  `;
};

export const updateSlideContent = async (
  slide: Slide,
  updateRequest: string,
  theme: Theme,
  aspectRatio: AspectRatio,
  apiKey: string,
  language: 'ko' | 'en'
): Promise<Slide> => {
  try {
    const updatedSlide = await updateSlideWithGemini(
      slide.title,
      slide.content,
      updateRequest,
      apiKey,
      language
    );

    const layout = SLIDE_LAYOUTS[slide.order % SLIDE_LAYOUTS.length];

    return {
      ...slide,
      title: updatedSlide.title,
      content: updatedSlide.content,
      htmlContent: generateSlideHTML(
        updatedSlide.title, 
        updatedSlide.content, 
        theme, 
        aspectRatio, 
        slide.order,
        undefined, // themeFont
        undefined, // borderStyle
        layout,
        slide.backgroundImage,
        slide.backgroundBlur || 2,
        slide.themeOverlay || 0.3,
        undefined, // backgroundColor
        undefined, // backgroundPattern
        undefined, // elements
        'ppt' // 기본값
      ),
      aspectRatio,
    };
  } catch {  // Removed unused error variable
    // console.error('Failed to update slide with AI:', error);
    // Fallback to simple update
    const lines = updateRequest.split('\n').filter(line => line.trim());
    const title = lines[0] || slide.title;
    const content = lines.slice(1).join('\n') || updateRequest;
    const layout = SLIDE_LAYOUTS[slide.order % SLIDE_LAYOUTS.length];

    return {
      ...slide,
      title,
      content,
      htmlContent: generateSlideHTML(
        title, 
        content, 
        theme, 
        aspectRatio, 
        slide.order, 
        undefined, // themeFont
        undefined, // borderStyle
        layout, 
        slide.backgroundImage,
        slide.backgroundBlur || 2,
        slide.themeOverlay || 0.3,
        undefined, // backgroundColor
        undefined, // backgroundPattern
        undefined, // elements
        'ppt' // 기본값
      ),
      aspectRatio,
    };
  }
};